/* CUSTOM_ANALYTICS_BY_CLAUDE — Sales Analytics hub dashboard
 * (Phases 1–3: Effort → Quotes → Sales).
 *
 * Mounts into #hub-analytics-root on /pages/sales-team-portal (the hub panel).
 * Data: rfq-worker GET /sales-analytics (server-side role-gated — reps receive
 * only their own block with no activity counts; admin receives all reps +
 * team totals). Charts: vendored Chart.js v4 UMD (chart.umd.min.js, loaded
 * before this file). Visual rules: tools/PORTAL-TOOL-STYLE-GUIDE.md.
 */
(function () {
  'use strict';

  // Timeline presets — mirrors the DD's date-preset select, plus the
  // "Last 30 Days" default (Ryan 2026-06-10). null = divider.
  var RANGE_OPTIONS = [
    { v: 'today', l: 'Today' },
    { v: 'year', l: 'This Year' },
    { v: 'quarter', l: 'This Quarter' },
    { v: 'month', l: 'This Month' },
    { v: 'week', l: 'This Week' },
    null,
    { v: 'lastyear', l: 'Last Year' },
    { v: 'lastquarter', l: 'Last Quarter' },
    { v: 'lastmonth', l: 'Last Month' },
    { v: 'lastweek', l: 'Last Week' },
    null,
    // CUSTOM_DATERANGES_BY_CLAUDE (Ryan 2026-10-10): the Marketing Ops list, same names and order.
    { v: 'last30', l: 'Last 30 days' },
    { v: 'last90', l: 'Last 90 days' },
    { v: 'last180', l: 'Last 180 days' },
    null,
    { v: 'all', l: 'All Time' },
  ];
  // 'emails' is computed: rep-logged email entries + Outlook-synced outbound
  // (manual email logging is discontinued — Outlook sync is the live source;
  // the logged count only carries historical entries).
  function emailCount(a) { return (a.email || 0) + (a.outlookOut || 0); }

  // state.metric: which Effort trend chart is shown — 'fr' | 'oh' | 'cu'
  // state.salesMetric: which Sales trend chart — 'sm' (sold/GP) | 'cr' | 'ad'
  // Defaults (Ryan 2026-06-10): Last 30 Days; admin rep defaults to JW.
  // state.quotesMetric: Quotes tab — 'pace' | 'qo' (quoted $/bucket) | 'bl'
  // (backlog over time); falls back to 'qo' when the rep has no pace goal.
  var state = { range: 'last30', rep: 'ALL', repInitialized: false, metric: 'fr', salesMetric: 'sm', quotesMetric: 'qo', data: null, loading: false };
  // Weekly Snapshot report card (admin-only) — its own rep + week, independent
  // of the page timeline. week = null means the current (in-progress) week.
  var snap = { rep: null, week: null, card: null };
  var root = null;
  var charts = [];
  // Which section's tab click caused this render — only THAT section's chart
  // replays its entry animation; the others redraw instantly (Ryan
  // 2026-06-10). 'all' = full-page loads / rep / range changes.
  var animateSection = 'all';
  function animDuration(section) {
    return (animateSection === 'all' || animateSection === section) ? undefined : 0;
  }

  /* ---------------- helpers ---------------- */

  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function dstr(d) { return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); }

  function rangeDates(key) {
    var now = new Date();
    var y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    var from, to = now;
    // Sunday-based weekday index (0 = Sun) so This Week / Last Week run Sun-Sat,
    // matching the Weekly Snapshot report card (Ryan 2026-06-20). The weekly
    // trend-chart buckets stay Monday-keyed (ISO, server-side) — separate.
    var dow = now.getDay();
    switch (key) {
      case 'today': from = now; break;
      case 'week': from = new Date(y, m, d - dow); break;
      case 'month': from = new Date(y, m, 1); break;
      case 'quarter': from = new Date(y, Math.floor(m / 3) * 3, 1); break;
      case 'year': from = new Date(y, 0, 1); break;
      case 'lastweek':
        to = new Date(y, m, d - dow - 1);
        from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - 6);
        break;
      case 'lastmonth': from = new Date(y, m - 1, 1); to = new Date(y, m, 0); break;
      case 'lastquarter': {
        var qs = Math.floor(m / 3) * 3 - 3;
        from = new Date(y, qs, 1);
        to = new Date(y, qs + 3, 0);
        break;
      }
      case 'lastyear': from = new Date(y - 1, 0, 1); to = new Date(y - 1, 11, 31); break;
      case 'all': from = new Date(2026, 4, 1); break; // CRM go-live 2026-05-01
      case 'last90': from = new Date(now.getTime() - 89 * 86400000); break;
      case 'last180': from = new Date(now.getTime() - 179 * 86400000); break;
      case 'last30':
      case '30d':
      default: from = new Date(now.getTime() - 29 * 86400000); break;
    }
    return { from: dstr(from), to: dstr(to) };
  }

  function fmtDur(ms) {
    if (ms == null || !isFinite(ms)) return '—';
    if (ms < 60000) return '<1m';
    var mins = Math.round(ms / 60000);
    if (mins < 60) return mins + 'm';
    var h = ms / 3600000;
    if (h < 24) {
      // over 10h the minutes are noise — hours only (Ryan 2026-06-10)
      if (h >= 10) return Math.round(h) + 'h';
      /* Ryan 2026-06-13: derive h+m from the already-rounded integer minutes,
         NOT from (h - floor(h)) * 60 — the float path rounded e.g. 3h59.7m to
         "3h 60m" instead of carrying to "4h". mins % 60 can never hit 60. */
      var whole = Math.floor(mins / 60);
      var rem = mins % 60;
      return rem > 0 ? whole + 'h ' + rem + 'm' : whole + 'h';
    }
    // 'Xd Yh' (Ryan 2026-06-10 — was decimal '1.2d')
    var totalH = Math.round(h);
    var days = Math.floor(totalH / 24);
    var remH = totalH % 24;
    return remH > 0 ? days + 'd ' + remH + 'h' : days + 'd';
  }

  // Hours-only variant — never rolls into days ('63h 15m', not '2d 15h').
  // Off-hold durations use this so they read against the 8-HOUR goal.
  function fmtDurH(ms) {
    if (ms == null || !isFinite(ms)) return '—';
    if (ms < 60000) return '<1m';
    var mins = Math.round(ms / 60000);
    if (mins < 60) return mins + 'm';
    var h = Math.floor(mins / 60);
    // over 10h the minutes are noise — hours only (Ryan 2026-06-10)
    if (h >= 10) return Math.round(mins / 60) + 'h';
    var m = mins % 60;
    return m > 0 ? h + 'h ' + m + 'm' : h + 'h';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // '8–6 · Mon–Fri' — derived from the server config so the label can
  // never disagree with the clock the averages are computed on.
  function bizHoursLabel(cfg) {
    var bh = cfg && cfg.businessHours;
    var DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    var NAMES = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
    var present = bh ? DAYS.filter(function (d) { return bh[d]; }) : [];
    if (!present.length) return 'business hours';
    var win = bh[present[0]];
    var days = NAMES[present[0]] + (present.length > 1 ? '–' + NAMES[present[present.length - 1]] : '');
    // bare clock numbers ('8–6'), no am/pm (Ryan 2026-06-10)
    var hr12 = function (h) { var v = h % 12; return v === 0 ? 12 : v; };
    return hr12(win[0]) + '–' + hr12(win[1]) + ' · ' + days;
  }

  // RAG bands (planner): green >= 80% within goal, yellow 60-79, red < 60.
  function ragForPct(pct) {
    if (pct == null) return '';
    if (pct >= 80) return 'green';
    if (pct >= 60) return 'yellow';
    return 'red';
  }

  // Friday Clean-Up RAG: binary — green at/under goal, red over (Ryan
  // 2026-06-10: dropped the early-week amber grace; matches the chart line).
  function cleanupRag(total, goal) {
    return total <= goal ? 'green' : 'red';
  }

  function cssVar(name, fallback) {
    var wrap = document.querySelector('.rep-quote-portal-wrap') || document.documentElement;
    var v = getComputedStyle(wrap).getPropertyValue(name).trim();
    return v || fallback;
  }

  /* ---------------- data ---------------- */

  function load() {
    if (!window.RepPortal || !window.RepPortal.API) return;
    var dates = rangeDates(state.range);
    state.loading = true;
    renderStatus('Loading analytics…', false, true);
    window.RepPortal.API.get('/sales-analytics?from=' + dates.from + '&to=' + dates.to)
      .then(function (data) {
        state.data = data;
        state.loading = false;
        // Non-admin payloads carry exactly one rep — pin the selector to it.
        var codes = Object.keys(data.reps || {});
        if (!data.totals) {
          state.rep = codes[0] || 'ALL';
        } else {
          // Admin first load defaults to JW (Ryan 2026-06-10).
          if (!state.repInitialized) {
            state.rep = codes.indexOf('JW') !== -1 ? 'JW' : 'ALL';
          } else if (state.rep !== 'ALL' && codes.indexOf(state.rep) === -1) {
            state.rep = 'ALL';
          }
        }
        state.repInitialized = true;
        render();
      })
      .catch(function (err) {
        state.loading = false;
        // 403 = authenticated but not a rep/admin (CFA, agency) — quiet note,
        // not a red error. Agency sessions normally never reach this page
        // (custom-rep-portal.js redirects them), so this is a belt-and-
        // suspenders for other restricted roles.
        if (err && err.status === 403) {
          renderStatus('Sales Analytics isn’t enabled for your account.', false);
          return;
        }
        renderStatus('Could not load analytics — ' + esc((err && err.message) || 'request failed'), true);
      });
  }

  /* ---------------- rendering ---------------- */

  function renderStatus(msg, isError, isLoading) {
    if (!root) return;
    destroyCharts();
    // Loading state mirrors Marketing Ops' .moph-updating: blue text + blue
    // spinning ring (Ryan 2026-06-10).
    root.innerHTML = '<div class="hub-analytics__status' +
      (isError ? ' hub-analytics__status--error' : '') +
      (isLoading ? ' hub-analytics__status--loading' : '') + '" role="status">' +
      (isLoading ? '<span class="hub-analytics__spinner" aria-hidden="true"></span>' : '') +
      msg + '</div>';
  }

  function destroyCharts() {
    charts.forEach(function (c) { try { c.destroy(); } catch (e) { /* noop */ } });
    charts = [];
  }

  // '$1.23M' / '$45k' / '$310'
  function fmtUsd(v) {
    if (v == null || !isFinite(v)) return '—';
    if (v >= 1000000) return '$' + (Math.round(v / 10000) / 100) + 'M';
    if (v >= 1000) return '$' + Math.round(v / 1000) + 'k';
    return '$' + Math.round(v);
  }

  // Extra-wide card holding a stat pair; with a key, clicking switches which
  // trend chart renders below (Ryan 2026-06-10 grouping). key=null → static.
  // stateKey: which state field tracks the active card ('metric' for Effort,
  // 'salesMetric' for the Sales section — each master card has its own chart).
  // foot: optional full-width line under the stats — used when a tab's
  // context + goal read as ONE long line instead of per-stat subs (Ryan
  // 2026-06-10; per-stat subs are half-card and would truncate).
  // link: optional {href, label} — a drill-through to where the task gets
  // WORKED (Ryan 2026-06-10: the Effort card is the rep's 4-task list; every
  // tab opens its door in the DD).
  // whyKey: optional WHY_PANES key — renders the tab title as a clickable
  // underlined header that opens the research slide-out (Ryan 2026-06-11).
  function groupCard(key, title, stats, stateKey, foot, link, whyKey) {
    var sk = stateKey || 'metric';
    var active = key && state[sk] === key;
    var html = '<div class="hub-analytics__group' + (active ? ' hub-analytics__group--active' : '') +
      (key ? '' : ' hub-analytics__group--static') +
      '"' + (key ? ' data-' + (sk === 'metric' ? 'metric' : sk.toLowerCase()) + '="' + key + '" role="button" tabindex="0" aria-pressed="' + (active ? 'true' : 'false') + '"' : '') + '>';
    html += '<p class="hub-analytics__group-title">' +
      (whyKey
        ? '<span class="hub-analytics__group-why" data-why="' + whyKey + '" title="Why this matters">' + title + '</span>'
        : '<span>' + title + '</span>') +
      (link ? '<a class="hub-analytics__group-link" href="' + esc(link.href) + '">' + esc(link.label) + ' &rarr;</a>' : '') + '</p>';
    html += '<div class="hub-analytics__group-stats' + (stats.length === 1 ? ' hub-analytics__group-stats--single' : '') + '">';
    stats.forEach(function (st) {
      html += '<div class="hub-analytics__stat' + (st.rag ? ' hub-analytics__stat--' + st.rag : '') + '">' +
        '<p class="hub-analytics__card-label">' + st.label + '</p>' +
        '<p class="hub-analytics__card-value">' + st.value + '</p>' +
        (st.sub ? '<p class="hub-analytics__card-sub">' + st.sub + '</p>' : '') +
        '</div>';
    });
    html += '</div>';
    if (foot) html += '<p class="hub-analytics__group-foot">' + foot + '</p>';
    return html + '</div>';
  }

  /* WHY slide-out panes (Ryan 2026-06-11) — each Effort task-tab header is
     clickable and opens a right-hand slide-out explaining why the metric
     exists, backed by the research. The premise the whole page is built on:
     Step 1 (Effort) is the controllable foundation — Steps 2 and 3 follow
     from it. Stats below were verified against the cited studies 2026-06-11;
     keep them faithful to the sources, don't punch them up. */
  var WHY_PANES = {
    fr: {
      title: 'First Outreach',
      lead: 'A brand-new inquiry is a perishable asset — the odds of ever reaching that person collapse within minutes, not days. Speed is the cheapest conversion lever we have.',
      studies: [
        { stat: 'Call a new lead within 5 minutes and you are 100× more likely to reach them — and 21× more likely to qualify them — than if you wait just 30 minutes.', src: 'MIT / InsideSales.com Lead Response Management Study (Prof. James Oldroyd)' },
        { stat: 'Companies that made contact within 1 hour were ~7× more likely to qualify the lead than those even an hour slower — and 60×+ more likely than those who waited 24 hours.', src: 'Harvard Business Review, "The Short Life of Online Sales Leads" (2011) — audit of 2,241 companies' },
        { stat: 'A first call attempt within 1 minute of the lead arriving lifted conversion by 391%.', src: 'Velocify sales optimization study (~3.5M leads)' },
      ],
      goal: 'Our goal: first outreach within 1 business hour. The clock starts the moment the lead lands.',
    },
    oh: {
      title: 'Follow-Up Response',
      lead: 'Deals are rarely won on the first touch. They are won by whoever keeps showing up — on schedule, without going dark.',
      studies: [
        { stat: '93% of leads that eventually convert are reached by the 6th call attempt. Most reps quit long before that — the conversions hide in the later touches.', src: 'Velocify, "The Ultimate Contact Strategy" (~3.5M leads)' },
        { stat: 'Simply timing follow-up attempts well lifted conversion ~49%, and emailing between calls raised the odds of getting them on the phone another 16%.', src: 'Velocify, "The Ultimate Contact Strategy"' },
        { stat: 'Persistence pays on a schedule, not in bursts: once a lead has sat untouched ~20 hours, each additional dial makes qualifying LESS likely. Work them while they are warm.', src: 'MIT / InsideSales.com Lead Response Management Study' },
      ],
      goal: 'Our goal: answer every off-hold lead within 8 business hours of its timer expiring.',
    },
    cu: {
      title: 'Money Moves',
      lead: 'This is the leak detector. Every open item here is a real person waiting on us — and the research is blunt about what silence costs.',
      studies: [
        { stat: 'In Harvard’s audit of 2,241 companies, 23% never responded to their leads at all. Nobody decides to ignore a lead — it happens one overlooked follow-up at a time. This list exists so it never happens here.', src: 'Harvard Business Review, "The Short Life of Online Sales Leads" (2011)' },
        { stat: 'Leads that needed 7+ attempts before FIRST contact were 45% less likely to convert — the longer something sits open, the colder it gets.', src: 'Velocify (~3.5M leads)' },
      ],
      goal: 'Our goal: keep this list at zero — nothing waiting, nothing dropped, every bell answered same-day.',
    },
    ac: {
      title: 'Activity',
      lead: 'Outcomes lag effort. Calls, emails, texts, drop-ins, meetings — activity volume is the one funnel input a rep controls completely, and it shows up in results.',
      studies: [
        { stat: 'Across 25+ years of sales research, work engagement — sustained day-in, day-out effort — is one of only five factors with a statistically significant link to sales performance (β = .23), alongside selling knowledge and adaptiveness.', src: 'Verbeke, Dietz & Verwaal, Journal of the Academy of Marketing Science (2011) — meta-analysis of studies 1982–2008' },
        { stat: 'The classic meta-analysis of salesperson productivity found effort to be a direct driver of sales performance — the input most within a rep’s own control.', src: 'Brown & Peterson, Journal of Marketing (1994)' },
      ],
      goal: 'Our goal: hit the daily activity target every business day. The pipeline you work this month is the revenue you close next quarter.',
    },
  };

  function whyHost() {
    return (root && root.closest('.rep-quote-portal-wrap')) || document.body;
  }
  function ensureWhyPane() {
    var host = whyHost();
    var pane = host.querySelector('.hub-analytics__why');
    if (pane) return pane;
    var ov = document.createElement('div');
    ov.className = 'hub-analytics__why-overlay';
    pane = document.createElement('aside');
    pane.className = 'hub-analytics__why';
    pane.setAttribute('role', 'dialog');
    pane.setAttribute('aria-modal', 'true');
    pane.setAttribute('aria-label', 'Why this matters');
    host.appendChild(ov);
    host.appendChild(pane);
    ov.addEventListener('click', closeWhyPane);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeWhyPane();
    });
    return pane;
  }
  function openWhyPane(key) {
    var c = WHY_PANES[key];
    if (!c) return;
    var pane = ensureWhyPane();
    var host = whyHost();
    var html = '<button type="button" class="hub-analytics__why-close" aria-label="Close">&times;</button>' +
      '<p class="hub-analytics__why-kicker">Why This Matters</p>' +
      '<h3 class="hub-analytics__why-title">' + c.title + '</h3>' +
      '<p class="hub-analytics__why-lead">' + c.lead + '</p>';
    c.studies.forEach(function (st) {
      html += '<div class="hub-analytics__why-study">' +
        '<p class="hub-analytics__why-stat">' + st.stat + '</p>' +
        '<p class="hub-analytics__why-src">' + st.src + '</p></div>';
    });
    html += '<p class="hub-analytics__why-goal">' + c.goal + '</p>';
    pane.innerHTML = html;
    pane.querySelector('.hub-analytics__why-close').addEventListener('click', closeWhyPane);
    host.querySelector('.hub-analytics__why-overlay').classList.add('hub-analytics__why-overlay--open');
    pane.classList.add('hub-analytics__why--open');
  }
  function closeWhyPane() {
    var host = whyHost();
    var pane = host.querySelector('.hub-analytics__why');
    var ov = host.querySelector('.hub-analytics__why-overlay');
    if (pane) pane.classList.remove('hub-analytics__why--open');
    if (ov) ov.classList.remove('hub-analytics__why-overlay--open');
  }

  /* ---------------- Weekly Snapshot report card (admin-only) ----------------
     One glanceable Sun-Sat card per rep for Ryan's Friday review. Each Effort
     metric carries a COLOR (level vs goal, from the server's verdict) and an
     ARROW (direction vs the trailing baseline); the score = greens / gradable.
     Centered modal, mirrors the WHY-pane open/close conventions. The modal is
     built to hold all three funnel stages — Quotes + Sales ride along as
     locked rows until Steps 2-3 ship. */

  function round1(n) {
    if (n == null || !isFinite(n)) return '—';
    return (Math.round(n * 10) / 10).toString();
  }

  // YYYY-MM-DD ± n days (UTC math — date-only, no tz drift).
  function addDaysStr(dateStr, n) {
    var p = String(dateStr).split('-').map(Number);
    var dt = new Date(Date.UTC(p[0], p[1] - 1, p[2]));
    dt.setUTCDate(dt.getUTCDate() + n);
    var pad = function (x) { return x < 10 ? '0' + x : '' + x; };
    return dt.getUTCFullYear() + '-' + pad(dt.getUTCMonth() + 1) + '-' + pad(dt.getUTCDate());
  }

  // Arrow paints by good/bad direction (server normalizes polarity), so up is
  // always green/improving regardless of whether the underlying value rose.
  function snapArrow(dir, gradable) {
    if (!gradable) return '';
    // One glyph for all three (↗), rotated via CSS so the stroke weight/length
    // is identical — the straight → / ↘ Unicode glyphs render thinner/longer.
    var map = {
      up: { g: '&nearr;', c: 'up', t: 'Improving vs recent weeks' },
      down: { g: '&nearr;', c: 'down', t: 'Drifting vs recent weeks' },
      flat: { g: '&nearr;', c: 'flat', t: 'Holding steady' },
    };
    var a = map[dir] || map.flat;
    return '<span class="hub-analytics__snap-arrow hub-analytics__snap-arrow--' + a.c + '" title="' + a.t + '">' + a.g + '</span>';
  }

  function snapMetricRow(m) {
    var valueLabel;
    if (!m.gradable) valueLabel = '—';
    else if (m.key === 'fr') valueLabel = fmtDur(m.avgMs);
    else if (m.key === 'oh') valueLabel = fmtDurH(m.avgMs);
    else if (m.key === 'cu') valueLabel = m.openNow + ' open';
    else if (m.key === 'ac') valueLabel = m.total + ' / ' + m.weeklyGoal;
    else if (m.key === 'qo') valueLabel = fmtUsd(m.quotedUsd) + (m.weeklyTarget != null ? ' / ' + fmtUsd(m.weeklyTarget) : '');
    else if (m.key === 'bl') valueLabel = m.openNow + ' open';
    else if (m.key === 'so') valueLabel = fmtUsd(m.soldUsd) + (m.weeklyTarget != null ? ' / ' + fmtUsd(m.weeklyTarget) : '');
    else if (m.key === 'gm') valueLabel = (m.marginPct != null ? m.marginPct + '%' : '—');
    else valueLabel = '—';
    var dotCls = m.color ? ('hub-analytics__snap-dot--' + m.color) : 'hub-analytics__snap-dot--na';
    return '<div class="hub-analytics__snap-metric">' +
      '<span class="hub-analytics__snap-dot ' + dotCls + '" aria-hidden="true"></span>' +
      '<span class="hub-analytics__snap-metric-label">' + esc(m.label) +
        '<span class="hub-analytics__snap-metric-goal">' + esc(m.goalLabel) + '</span></span>' +
      '<span class="hub-analytics__snap-metric-val">' + esc(valueLabel) + '</span>' +
      snapArrow(m.arrow, m.gradable) +
      '</div>';
  }

  function snapStage(stage) {
    if (!stage.live) {
      var step = stage.key === 'sales' ? 3 : (stage.key === 'quotes' ? 2 : 1);
      return '<div class="hub-analytics__snap-stage hub-analytics__snap-stage--locked">' +
        '<span class="hub-analytics__snap-stage-name">' + esc(stage.label) + '</span>' +
        '<span class="hub-analytics__snap-soon">Coming with Step ' + step + '</span>' +
        '</div>';
    }
    var sc = stage.score;
    var pending = (sc.of || sc.gradable) - sc.gradable;
    var note = pending > 0 ? '<span class="hub-analytics__snap-pending">' + pending + ' metric' + (pending === 1 ? '' : 's') + ' need more data this week</span>' : '';
    var rows = stage.metrics.map(snapMetricRow).join('');
    return '<div class="hub-analytics__snap-stage">' +
      '<div class="hub-analytics__snap-stage-head">' +
        '<span class="hub-analytics__snap-stage-name">' + esc(stage.label) + '</span>' +
        '<span class="hub-analytics__snap-score">' + sc.green +
          '<span class="hub-analytics__snap-score-of">/' + sc.gradable + '</span></span>' +
      '</div>' +
      '<div class="hub-analytics__snap-metrics">' + rows + '</div>' +
      note +
      '</div>';
  }

  function snapBody(card) {
    return (card.stages || []).map(snapStage).join('');
  }

  function ensureSnapPane() {
    var host = whyHost();
    var existing = host.querySelector('.hub-analytics__snap');
    if (existing) return existing;
    var ov = document.createElement('div');
    ov.className = 'hub-analytics__snap-overlay';
    var modal = document.createElement('div');
    modal.className = 'hub-analytics__snap';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Weekly Snapshot');
    host.appendChild(ov);
    host.appendChild(modal);
    ov.addEventListener('click', closeSnapshot);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeSnapshot(); });
    return modal;
  }

  function renderSnapshot(card, loading, error) {
    var modal = whyHost().querySelector('.hub-analytics__snap');
    if (!modal) return;
    var codes = Object.keys((state.data && state.data.reps) || {});
    var label = card ? card.week.label : '…';
    var canNext = card ? !card.isCurrent : false;

    // Rep picker only when there's more than one rep (admins). A rep viewing
    // their own card doesn't need to be told who they are.
    var repSelHtml = codes.length > 1
      ? '<select class="hub-analytics__select hub-analytics__snap-rep" aria-label="Rep">' +
          codes.map(function (c) { return '<option value="' + esc(c) + '"' + (c === snap.rep ? ' selected' : '') + '>' + esc(c) + '</option>'; }).join('') +
        '</select>'
      : '<span class="hub-analytics__snap-onlyrep">' + esc(snap.rep || (codes[0] || '')) + '</span>';

    var head = '<div class="hub-analytics__snap-head">' +
      repSelHtml +
      '<div class="hub-analytics__snap-week">' +
        '<button type="button" class="hub-analytics__snap-nav" data-snap-nav="prev" aria-label="Previous week">&lsaquo;</button>' +
        '<span class="hub-analytics__snap-week-label">' + esc(label) + '</span>' +
        '<button type="button" class="hub-analytics__snap-nav" data-snap-nav="next"' + (canNext ? '' : ' disabled') + ' aria-label="Next week">&rsaquo;</button>' +
      '</div>' +
      '<button type="button" class="hub-analytics__snap-close" aria-label="Close">&times;</button>' +
      '</div>';

    // Week-progress bar — how far into the Sun-Sat week we are (full = done).
    var prog = '';
    if (card) {
      var pct = Math.max(0, Math.min(100, card.weekProgress || 0));
      prog = '<div class="hub-analytics__snap-progress">' +
        '<div class="hub-analytics__snap-progress-track"><div class="hub-analytics__snap-progress-fill" style="width:' + pct + '%"></div></div>' +
        '<span class="hub-analytics__snap-progress-label">' + (card.frozen ? 'Week complete' : pct + '% through the week') + '</span>' +
        '</div>';
    }

    var body;
    if (loading) body = '<div class="hub-analytics__snap-msg">Loading…</div>';
    else if (error) body = '<div class="hub-analytics__snap-msg">' + esc(error) + '</div>';
    else if (card && card.locked) body = '<div class="hub-analytics__snap-msg">&#128274; This week\'s report card unlocks <strong>Friday</strong>.<br>Check back then, or use &lsaquo; to look at a past week.</div>';
    else body = snapBody(card);

    modal.innerHTML = head + prog + '<div class="hub-analytics__snap-scroll">' + body + '</div>';
    modal.querySelector('.hub-analytics__snap-close').addEventListener('click', closeSnapshot);
    var repSel = modal.querySelector('.hub-analytics__snap-rep');
    if (repSel) repSel.addEventListener('change', function () { snap.rep = repSel.value; snap.week = null; fetchSnapshot(); });
    modal.querySelectorAll('[data-snap-nav]').forEach(function (el) {
      el.addEventListener('click', function () {
        if (el.disabled || !snap.card) return;
        snap.week = el.getAttribute('data-snap-nav') === 'prev'
          ? addDaysStr(snap.card.week.from, -1)   // Saturday of the prior week
          : addDaysStr(snap.card.week.to, 1);      // Sunday of the next week
        fetchSnapshot();
      });
    });
  }

  function fetchSnapshot() {
    if (!snap.rep || !window.RepPortal || !window.RepPortal.API) return;
    renderSnapshot(null, true, null);
    var q = '/sales-analytics/weekly-report?rep=' + encodeURIComponent(snap.rep) +
      (snap.week ? '&week=' + encodeURIComponent(snap.week) : '');
    window.RepPortal.API.get(q).then(function (card) {
      snap.card = card;
      renderSnapshot(card, false, null);
    }).catch(function (e) {
      renderSnapshot(null, false, (e && e.message) || 'Could not load this snapshot.');
    });
  }

  function openSnapshotModal() {
    var d = state.data;
    if (!d || !d.reps) return;
    var codes = Object.keys(d.reps);
    if (!codes.length) return;
    snap.rep = (state.rep && state.rep !== 'ALL' && codes.indexOf(state.rep) !== -1)
      ? state.rep
      : (codes.indexOf('JW') !== -1 ? 'JW' : codes[0]);
    snap.week = null;
    snap.card = null;
    ensureSnapPane();
    var host = whyHost();
    host.querySelector('.hub-analytics__snap-overlay').classList.add('hub-analytics__snap-overlay--open');
    host.querySelector('.hub-analytics__snap').classList.add('hub-analytics__snap--open');
    fetchSnapshot();
  }

  function closeSnapshot() {
    var host = whyHost();
    var modal = host.querySelector('.hub-analytics__snap');
    var ov = host.querySelector('.hub-analytics__snap-overlay');
    if (modal) modal.classList.remove('hub-analytics__snap--open');
    if (ov) ov.classList.remove('hub-analytics__snap-overlay--open');
  }

  // Downward funnel-flow arrow between master cards (Effort ↓ Quotes ↓
  // Sales). The div doubles as the spacer: 18px tall + the root's two 18px
  // flex gaps = the same 54px card-to-card rhythm.
  function flowArrow() {
    return '<div class="hub-analytics__flow-arrow" aria-hidden="true">' +
      '<svg width="18" height="18" viewBox="0 0 18 18"><path d="M9 3v10M4.5 8.5L9 13l4.5-4.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></div>';
  }

  function ragBadge(pct) {
    if (pct == null) return '<span class="hub-analytics__rag hub-analytics__rag--na">—</span>';
    return '<span class="hub-analytics__rag hub-analytics__rag--' + ragForPct(pct) + '">' + pct + '%</span>';
  }

  function selectedStats() {
    var d = state.data;
    if (state.rep === 'ALL') return d.totals || d.reps[Object.keys(d.reps)[0]];
    return d.reps[state.rep];
  }

  function render() {
    if (!root || !state.data) return;
    destroyCharts();
    var d = state.data;
    var isAdmin = !!d.totals;
    var s = selectedStats();
    if (!s) { renderStatus('No data for this selection.'); return; }

    var html = '';

    // Global controls — rendered into the page-head row NEXT TO the title
    // (Ryan 2026-06-10: title sits at the same height as the dropdowns).
    // Living outside #hub-analytics-root, they also survive the
    // "Loading analytics…" swap on range changes instead of flickering away.
    var controlsHtml = '<div class="hub-analytics__controls">';
    if (isAdmin) {
      controlsHtml += '<select class="hub-analytics__select" data-ctl="rep" aria-label="Team member">' +
        '<option value="ALL"' + (state.rep === 'ALL' ? ' selected' : '') + '>All Team</option>';
      Object.keys(d.reps).forEach(function (c) {
        controlsHtml += '<option value="' + esc(c) + '"' + (state.rep === c ? ' selected' : '') + '>' + esc(c) + '</option>';
      });
      controlsHtml += '</select>';
    }
    controlsHtml += '<select class="hub-analytics__select" data-ctl="range" aria-label="Timeline">';
    RANGE_OPTIONS.forEach(function (o) {
      if (!o) { controlsHtml += '<option disabled>──────────────</option>'; return; }
      controlsHtml += '<option value="' + o.v + '"' + (state.range === o.v ? ' selected' : '') + '>' + esc(o.l) + '</option>';
    });
    controlsHtml += '</select>';
    // Weekly Snapshot — one-click report card. Admins pick any rep; reps see
    // their own (server-scoped + Friday-gated). Shown to everyone on the hub.
    controlsHtml += '<button type="button" class="hub-analytics__snap-btn" data-ctl="snapshot">Weekly Snapshot</button>';
    controlsHtml += '</div>';
    var ctlMount = document.getElementById('hub-analytics-pagehead-controls');
    if (ctlMount) ctlMount.innerHTML = controlsHtml;
    else html += '<div class="hub-analytics__head"><span></span>' + controlsHtml + '</div>'; // stale-cached liquid fallback

    /* ===== EFFORT master card ===== */
    html += '<div class="rep-quote-hub-panel hub-analytics__panel">' +
      '<p class="rep-quote-hub-panel__title">Step 1: Effort</p>' +
      '<p class="rep-quote-hub-panel__subtitle">Response speed &amp; volume</p>';

    // Stat cards
    var fr = s.firstResponse || {};
    var oh = s.offHold || {};
    var leads = s.leads || {};
    var smallNote = function (o) { return 'needs ' + o.needed + ' more data point' + (o.needed === 1 ? '' : 's'); };

    // Goal hours come from the server (EFFORT_GOALS) so a worker-side goal
    // change updates every label here with no theme edit.
    var goals = d.goals || {};
    var frGoalH = Math.round((goals.firstResponseGoalMs || 2 * 3600000) / 3600000);
    var ohGoalH = Math.round((goals.offHoldGoalMs || 8 * 3600000) / 3600000);
    // TRUE MEANS (Ryan 2026-06-10, reversing the original median decision):
    // a median lets a rep hide ignored-lead tails behind a fast middle.
    // Context + goal merge into ONE full-width foot line per tab (Ryan
    // 2026-06-10); per-stat subs only carry the no-data fallbacks.
    var frStats = [
      {
        label: 'Avg Outreach', value: fmtDur(fr.avgBizMs),
        sub: fr.avgBizMs != null ? null : (fr.sample ? smallNote(fr) : 'no responded leads in range'),
        rag: ragForPct(fr.withinGoalPct),
      },
      {
        label: 'Within ' + frGoalH + ' Hours', value: fr.withinGoalPct != null ? fr.withinGoalPct + '%' : '—',
        sub: null,
        rag: ragForPct(fr.withinGoalPct),
      },
    ];
    var frFoot = fr.avgBizMs != null
      ? bizHoursLabel(d.config) + ' · Goal: Act on 🔥 in <' + frGoalH + ' Hour' + (frGoalH === 1 ? '' : 's')
      : null;
    var ohStats = [
      {
        label: 'Avg Response', value: fmtDurH(oh.avgMs),
        sub: oh.avgMs != null ? null : (oh.sample ? smallNote(oh) : 'no expired timers acted on'),
        rag: ragForPct(oh.withinGoalPct),
      },
      {
        label: 'Within ' + ohGoalH + ' Hours', value: oh.withinGoalPct != null ? oh.withinGoalPct + '%' : '—',
        sub: null,
        rag: ragForPct(oh.withinGoalPct),
      },
    ];
    var ohFoot = oh.avgMs != null
      ? 'Goal: Act on 🔔 in <' + ohGoalH + ' Hours'
      : null;
    // Friday Clean-Up (Ryan 2026-06-10): combined open backlog vs ≤goal by
    // Fri EOD. Counts are CURRENT STATE (not range-scoped); trend follows
    // the selected range.
    var cu = s.cleanup || { openNew: 0, openBells: 0, openTotal: 0, trend: { points: [] } };
    var cuGoal = cu.goal != null ? cu.goal : 3;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — two stats side by side:
       Avg Open Items (mean of the window's per-bucket backlog counts, same
       data the trend chart plots) + Currently Open. RAG verdict comes from
       the AVERAGE; Currently Open mirrors it (KISS — one verdict per tab).
       The breakdown sub-line moved to the group foot so it still spans the
       full tab width. */
    var _cuPts = (cu.trend && cu.trend.points) || [];
    var _cuAvg = _cuPts.length
      ? Math.round((_cuPts.reduce(function (s2, p) { return s2 + (p.count || 0); }, 0) / _cuPts.length) * 10) / 10
      : null;
    var _cuRag = cleanupRag(_cuAvg != null ? _cuAvg : cu.openTotal, cuGoal);
    var cuStats = [
      { label: 'Avg Open Items', value: _cuAvg != null ? String(_cuAvg) : '—', rag: _cuRag },
      { label: 'Currently Open', value: String(cu.openTotal), rag: _cuRag },
    ];
    var cuFoot = cu.openNew + ' New · ' + cu.openBells + ' Off-Hold · Goal ≤' + cuGoal;

    // Activity tab (admin-only — the server strips activity for reps).
    // Replaces the old chip row; also covers the All Team rollup now.
    var act = isAdmin && s.activity ? s.activity : null;
    if (state.metric === 'ac' && !act) state.metric = 'fr';

    // Trend chart for the SELECTED group (click a group tab to switch).
    var isCU = state.metric === 'cu';
    var isAC = state.metric === 'ac';
    var isFR = !isCU && !isAC && state.metric !== 'oh';
    var activeTrend = isAC ? (s.activityTrend || { points: [] })
      : isCU ? cu.trend : (isFR ? fr.trend : oh.trend);
    var getTrendVal = isAC
      ? function (p) { var t = p.call + p.email + p.text + p.dropin + p.meeting; return t > 0 ? t : null; }
      : (isCU ? function (p) { return p.count; } : function (p) { return p.avgMs; });
    var trendPts = (activeTrend && activeTrend.points) || [];
    var trendHasData = trendPts.some(function (p) { return getTrendVal(p) != null; });

    var acStats = null, acFoot = null;
    var actGoalDay = goals.activityPerDayGoal || 0;
    if (act) {
      var actTotal = (act.call || 0) + emailCount(act) + (act.text || 0) + (act.dropin || 0) + (act.meeting || 0);
      // Verdict vs the per-business-day goal: window total ÷ weekdays in
      // range ≥ goal → green. Volume is fully rep-controlled, so binary.
      var bizDays = weekdaysBetween(d.window.from, d.window.to);
      var acRag = '';
      if (actGoalDay > 0 && bizDays > 0) {
        acRag = (actTotal / bizDays) >= actGoalDay ? 'green' : 'red';
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-11) — Avg + Total, shared
         RAG from the average (same two-stat shape as Money Moves). The
         'Deals Touched' coverage stat + its 80/60 bands were CUT: the
         anti-neglect job belongs to Money Moves, which names the specific
         neglected deals instead of compressing them into a percentage —
         and coverage read RED during legitimate deep-focus stretches
         (3-day all-consuming project), training reps to distrust the card.
         Goal moved to the group foot (full width). */
      var _acAvg = bizDays > 0 ? Math.round((actTotal / bizDays) * 10) / 10 : null;
      /* 'Per Business Day' sub + 'Goal: ≥X/Day' foot combined into ONE foot
         line (Ryan 2026-06-12) — the two stacked lines read as clutter. */
      acStats = [
        { label: 'Avg Activities', value: _acAvg != null ? String(_acAvg) : '—', rag: acRag },
        { label: 'Total Activities', value: String(actTotal), rag: acRag },
      ];
      acFoot = actGoalDay > 0 ? 'Mon-Fri – Goal: ≥' + actGoalDay + '/Day' : 'Mon-Fri';
    }

    // Folder-tab grouping (Ryan 2026-06-10): the cards sit ON the chart as
    // tabs; the active one merges with the chart below into one blue-outlined
    // folder shape. Falls back to plain cards when the range has no chart.
    // Each tab is a TASK — 'Work It' opens the DD view where it gets done.
    var ddRepQs = (isAdmin && state.rep !== 'ALL') ? '&rep=' + encodeURIComponent(state.rep) : '';
    var ddLink = function (status) {
      return { href: '/pages/rep-quote-dashboard' + (status ? '?status=' + status + ddRepQs : (ddRepQs ? '?' + ddRepQs.slice(1) : '')), label: 'Work It' };
    };
    html += '<div class="hub-analytics__tabbed">';
    html += '<div class="hub-analytics__groups' + (trendHasData ? ' hub-analytics__groups--tabs' : '') +
      (acStats ? ' hub-analytics__groups--four' : '') + '">' +
      groupCard('fr', 'First Outreach', frStats, null, frFoot, ddLink('awaiting'), 'fr') +
      groupCard('oh', 'Follow-Up Response', ohStats, null, ohFoot, ddLink('moneymoves'), 'oh') +
      groupCard('cu', 'Money Moves', cuStats, null, cuFoot, ddLink('moneymoves'), 'cu') +
      (acStats ? groupCard('ac', 'Activity', acStats, null, acFoot, ddLink(''), 'ac') : '') +
      '</div>';
    if (trendHasData) {
      var chartTitle = isAC
        ? 'Activity Volume'
        : isCU
          ? 'Open ' + (activeTrend.granularity === 'week' ? 'Weekly' : 'Daily') + ' Backlog'
          : (isFR ? 'Avg First Outreach Time' : 'Avg Off-Hold Response Time');
      html += '<div class="hub-analytics__chartwrap hub-analytics__chartwrap--trend hub-analytics__chartwrap--tabbed">' +
        '<p class="hub-analytics__chart-title">' + chartTitle + '</p>' +
        '<canvas id="hub-analytics-trend" role="img" aria-label="Chart: ' + chartTitle + '"></canvas></div>';
    }
    html += '</div>';

    html += '</div>'; // close EFFORT master card
    html += flowArrow(); // funnel flow: Effort ↓ Quotes

    /* ===== QUOTES master card (Phase 2) ===== */
    var qd = s.quotes || null;
    html += '<div class="rep-quote-hub-panel hub-analytics__panel">' +
      '<p class="rep-quote-hub-panel__title">Step 2: Quotes</p>' +
      '<p class="rep-quote-hub-panel__subtitle">Output &amp; pace</p>';
    if (qd) {
      var pace = qd.pace;
      var prRag = pace && pace.paceRatio != null ? ragForPct(pace.paceRatio * 100) : '';
      var pfRag = pace ? (pace.projectedFinish >= pace.annualTarget ? 'green'
        : pace.projectedFinish >= 0.8 * pace.annualTarget ? 'yellow' : 'red') : '';
      // Short per-stat subs + the shared context on a full-width foot line
      // (Effort-card pattern — half-card subs were truncating).
      var paceStats = pace ? [
        {
          label: 'Pace vs Goal', value: Math.round(pace.paceRatio * 100) + '%',
          sub: 'of ' + fmtUsd(pace.requiredToDate) + ' Goal',
          rag: prRag,
        },
        {
          label: 'Projected Finish', value: fmtUsd(pace.projectedFinish),
          sub: 'of ' + fmtUsd(pace.annualTarget) + ' Goal',
          rag: pfRag,
        },
      ] : [{
        label: 'Pace vs Goal', value: '—',
        sub: 'Set an Annual Goal in Settings → Sales Goals', rag: '',
      }];
      var paceFoot = pace
        ? fmtUsd(pace.actualYtd) + ' / ' + fmtUsd(pace.requiredToDate) + ' Quoted' +
          ' · Close Rate: ' + Math.round(qd.closeRate * 100) + '% (' + (qd.closeRateMode === 'actual' ? 'Actual' : 'Assumed') + ')'
        : null;
      // Needed/period: if every remaining window-sized period quoted this
      // much, the year goal lands (Ryan 2026-06-10). BOTH numbers binary:
      // green when this period's quoted >= needed, red when under.
      var nppRag = '';
      if (pace && pace.neededPerPeriod != null) {
        nppRag = qd.window.quotedUsd >= pace.neededPerPeriod ? 'green' : 'red';
      }
      var quoteStats = [
        {
          label: 'Quoted', value: fmtUsd(qd.window.quotedUsd),
          sub: qd.window.quoteCount + ' Quote' + (qd.window.quoteCount === 1 ? '' : 's') + ' Sent This Period',
          rag: nppRag,
        },
        {
          label: 'Needed / Period',
          value: pace && pace.neededPerPeriod != null ? fmtUsd(pace.neededPerPeriod) : '—',
          sub: pace && pace.neededPerPeriod != null
            ? pace.periodsLeft + ' Periods Left in Year'
            : 'Set an Annual Goal in Settings',
          rag: nppRag,
        },
      ];
      var quoteFoot = pace && pace.neededPerPeriod != null
        ? 'Goal: Quote ' + fmtUsd(pace.neededPerPeriod) + '/Period to Hit ' + fmtUsd(pace.annualTarget)
        : null;
      var blGoal = goals.backlogMaxOpen != null ? goals.backlogMaxOpen : 5;
      var backlogStats = [{
        label: 'Unquoted Backlog', value: String(qd.backlog.count),
        sub: qd.backlog.count > 0
          ? 'Deals at $0.00 · Oldest: ' + (qd.backlog.oldestDays != null ? qd.backlog.oldestDays + ' Days' : '—')
          : 'Every Worked Deal Has a Quote',
        rag: qd.backlog.count <= blGoal ? 'green' : 'red',
      }];
      var backlogFoot = 'Goal: ≤' + blGoal + ' Unquoted Deals';
      // Folder tabs, all three clickable (Ryan 2026-06-10): Annual Pace →
      // burn-up, Quote Output → quoted-$ per bucket bars, Backlog →
      // unquoted-count-over-time line. Goal-less reps have no pace chart, so
      // they default to Quote Output.
      var hasPaceChart = !!(pace && pace.trend && pace.trend.points.length);
      if (state.quotesMetric === 'pace' && !hasPaceChart) state.quotesMetric = 'qo';
      var qMetric = state.quotesMetric;
      var qoTrend = qd.quotedTrend || null;
      var blTrend = qd.backlogTrend || null;
      var quotesChartTitle = qMetric === 'pace'
        ? 'Annual Cumulative Quote Volume vs Required Pace'
        : (qMetric === 'qo'
          ? (qoTrend && qoTrend.granularity === 'week' ? 'Weekly' : 'Daily') + ' Quote Volume'
          : 'Unquoted Backlog Over Time');
      var hasQuotesChart = qMetric === 'pace' ? hasPaceChart
        : (qMetric === 'qo' ? !!(qoTrend && qoTrend.points.length) : !!(blTrend && blTrend.points.length));
      html += '<div class="hub-analytics__tabbed">';
      html += '<div class="hub-analytics__groups' + (hasQuotesChart ? ' hub-analytics__groups--tabs' : '') + '">' +
        /* Tab order (Ryan 2026-06-11): Output → Backlog → Pace — pace is the
           summary of doing the first two right, so it reads last. */
        groupCard(qoTrend ? 'qo' : null, 'Quote Output', quoteStats, 'quotesMetric', quoteFoot) +
        groupCard(blTrend ? 'bl' : null, 'Backlog', backlogStats, 'quotesMetric', backlogFoot) +
        groupCard(hasPaceChart ? 'pace' : null, 'Annual Pace', paceStats, 'quotesMetric', paceFoot) +
        '</div>';
      if (hasQuotesChart) {
        html += '<div class="hub-analytics__chartwrap hub-analytics__chartwrap--trend hub-analytics__chartwrap--tabbed">' +
          '<p class="hub-analytics__chart-title">' + quotesChartTitle + '</p>' +
          '<canvas id="hub-analytics-quotes" role="img" aria-label="Chart: ' + quotesChartTitle + '"></canvas></div>';
      }
      html += '</div>';
    } else {
      html += '<div class="hub-analytics__status">No quote data for this selection.</div>';
    }
    html += '</div>'; // close QUOTES master card
    html += flowArrow(); // funnel flow: Quotes ↓ Sales

    /* ===== SALES master card (Phase 3) ===== */
    var sd = s.sales || null;
    var sw = sd ? sd.window : null;
    html += '<div class="rep-quote-hub-panel hub-analytics__panel">' +
      '<p class="rep-quote-hub-panel__title">Step 3: Sales</p>' +
      '<p class="rep-quote-hub-panel__subtitle">Outcomes &amp; conversions</p>';
    if (sd) {
      // Sold verdict: binary vs the goal's prorated share of the year for
      // this window (annualTarget × windowDays/365) — same shape as the
      // Quote Output needed-per-period verdict. No goal → no verdict.
      var pace2 = qd && qd.pace;
      var soldRag = '';
      if (pace2 && pace2.annualTarget > 0) {
        var winDays = Math.max(1, Math.round((Date.parse(d.window.to) - Date.parse(d.window.from)) / 86400000) + 1);
        soldRag = sw.soldUsd >= pace2.annualTarget * winDays / 365 ? 'green' : 'red';
      }
      var soldGroupStats = [
        {
          label: 'Sold', value: fmtUsd(sw.soldUsd),
          sub: sw.soldCount + ' Deal' + (sw.soldCount === 1 ? '' : 's') + ' Won in Range',
          rag: soldRag,
        },
        {
          label: 'Gross Profit', value: fmtUsd(sw.gp),
          sub: sw.gpMarginPct != null ? sw.gpMarginPct + '% Margin on Sold $' : 'No Sales in Range',
          rag: soldRag, // GP inherits SOLD's verdict (Ryan 2026-06-10)
        },
      ];
      // YTD foot (Ryan 2026-06-11: total annual sold wasn't listed
      // anywhere). Summed from the monthly trend's calendar-year months —
      // closed months are snapshot-frozen, the current month is live.
      var smFoot = null;
      (function () {
        var mPts = (sd.monthly && sd.monthly.points) || [];
        var curYear = String(d.window.to || '').slice(0, 4);
        if (!curYear || !mPts.length) return;
        var ytdSold = 0, ytdGp = 0, any = false;
        mPts.forEach(function (p) {
          if (String(p.m).slice(0, 4) === curYear) {
            ytdSold += p.soldUsd || 0; ytdGp += p.gp || 0; any = true;
          }
        });
        if (!any) return;
        smFoot = 'YTD: ' + fmtUsd(ytdSold) + ' Sold · ' + fmtUsd(ytdGp) + ' GP' +
          (pace2 && pace2.annualTarget > 0 ? ' · Annual Goal: ' + fmtUsd(pace2.annualTarget) : '');
      })();
      // Close-rate v2 (Ryan 2026-06-10): won ÷ ALL deals that arrived in
      // the period — quoted, still-$0, open, lost all count against you.
      // Verdict vs the goal's ASSUMED rate (the rate the pace math uses).
      var coh = sd.cohort || { points: [], deals: 0, won: 0, lost: 0, rate: null, needed: 5, winRateValue: null };
      var crGoal = qd && qd.assumedCloseRate != null ? qd.assumedCloseRate : null;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-01) — headline is now the VALUE-based
         WIN RATE, identical to the DD "Win Rate" card: soldValue ÷ (soldValue + lostValue
         + openQuotedValue). The count-based cohort (won ÷ all deals arrived) stays as the
         "Deals Won" breakdown + the Won/Lost/Open outcome chart below. Verdict vs the
         plan's assumed rate (also a $ ratio — the pace math's sold$ ÷ quoted$). */
      var wr = coh.winRateValue;
      var crRag = '';
      if (wr != null && crGoal != null) crRag = wr >= crGoal ? 'green' : 'red';
      var crGroupStats = [
        {
          label: 'Win Rate',
          value: wr != null ? Math.round(wr * 100) + '%' : '—',
          sub: wr != null
            ? (fmtUsd(coh.soldValue || 0) + ' of ' + fmtUsd(coh.totalQuotedValue || 0) + (crGoal != null ? ' · Plan ' + Math.round(crGoal * 100) + '%' : ''))
            : 'no quoted value yet',
          rag: crRag,
        },
        {
          label: 'Deals Won', value: String(coh.won),
          sub: 'of ' + coh.deals + ' arrived (by count)',
          rag: '', // count breakdown — the value Win Rate above carries the verdict
        },
      ];
      // GP Margin tab (Ryan 2026-06-10, replacing Deal Velocity): the
      // counterweight to Sold/Close Rate — a rep can't go green here by
      // discounting. Bands: green ≥ goal (25%), amber ≥ floor (20%, allowed
      // without permission), red < floor (needed sign-off).
      var vel = sd.velocity || { points: [], avgDays: null, needed: 5 };
      var mGoal = goals.marginGoalPct != null ? goals.marginGoalPct : 25;
      var mFloor = goals.marginFloorPct != null ? goals.marginFloorPct : 20;
      var mRag = '';
      if (sw.gpMarginPct != null) {
        mRag = sw.gpMarginPct >= mGoal ? 'green' : (sw.gpMarginPct >= mFloor ? 'yellow' : 'red');
      }
      var adGroupStats = [
        {
          label: 'Avg GP Margin',
          value: sw.gpMarginPct != null ? sw.gpMarginPct + '%' : '—',
          sub: sw.gpMarginPct != null ? null : 'No Sales in Range',
          rag: mRag,
        },
        {
          label: 'Avg Deal Size',
          value: sw.avgDealUsd != null ? fmtUsd(sw.avgDealUsd) : '—',
          sub: null,
          rag: mRag, // mirrors Avg GP Margin (Ryan 2026-06-10)
        },
      ];
      // One full-width line flowing under BOTH numbers (Ryan 2026-06-10).
      var adFoot = (sw.soldCount > 0
        ? 'Average of ' + sw.soldCount + ' Sold Deal' + (sw.soldCount === 1 ? '' : 's') + ' in Range · '
        : '') + 'Goal: ≥' + mGoal + '% · Floor: ' + mFloor + '%';
      // Chart for the selected tab (folder look, like Effort). 'sm' = the
      // cumulative step chart; 'cr' = stacked outcome bars; 'ad' = per-deal
      // velocity bars.
      var salesChartMeta = {
        sm: 'Cumulative Sales Volume vs Required Pace',
        cr: 'Deal Outcomes by Arrival Date',
        ad: 'GP Margin per Deal',
      };
      var hasSalesChart = state.salesMetric === 'sm'
        ? !!(sd.soldCum && sd.soldCum.points && sd.soldCum.points.length)
        : (state.salesMetric === 'ad' ? vel.points.length > 0 : coh.deals > 0);
      html += '<div class="hub-analytics__tabbed">';
      html += '<div class="hub-analytics__groups' + (hasSalesChart ? ' hub-analytics__groups--tabs' : '') + '">' +
        /* Tab order (Ryan 2026-06-11, mirrors the Quotes card): components
           first, summary last — Win Rate → GP Margin → Sold & GP. Default
           selection stays 'sm' (the at-a-glance outcome). */
        groupCard('cr', 'Win Rate', crGroupStats, 'salesMetric') +
        groupCard('ad', 'GP Margin', adGroupStats, 'salesMetric', adFoot) +
        groupCard('sm', 'Sold &amp; GP', soldGroupStats, 'salesMetric', smFoot) +
        '</div>';
      if (hasSalesChart) {
        html += '<div class="hub-analytics__chartwrap hub-analytics__chartwrap--trend hub-analytics__chartwrap--tabbed">' +
          '<p class="hub-analytics__chart-title">' + salesChartMeta[state.salesMetric] + '</p>' +
          '<canvas id="hub-analytics-sales" role="img" aria-label="Chart: ' + salesChartMeta[state.salesMetric] + '"></canvas></div>';
      }
      html += '</div>';

    } else {
      html += '<div class="hub-analytics__status">No sales data for this selection.</div>';
    }
    html += '</div>'; // close SALES master card

    // Team table (admin, All Team view) — its own master card
    if (isAdmin && state.rep === 'ALL') {
      html += '<div class="rep-quote-hub-panel hub-analytics__panel">' +
        '<p class="rep-quote-hub-panel__title">Team</p>';
      html += '<div class="hub-analytics__tablewrap"><table class="hub-analytics__table">' +
        '<thead><tr><th>Rep</th><th>Leads</th><th>Avg Resp</th><th>&lt;' + frGoalH + 'h</th><th>No Resp</th>' +
        '<th>Off-Hold &lt;8h</th><th>Open Now</th><th>Quoted</th><th>Pace</th><th>Sold</th><th>Calls</th><th>Emails</th><th>Texts</th><th>Drop-ins</th></tr></thead><tbody>';
      Object.keys(d.reps).forEach(function (code) {
        var r = d.reps[code];
        var a = r.activity || {};
        var rcu = r.cleanup || { openTotal: 0 };
        var openCell = rcu.openTotal > cuGoal
          ? '<span class="hub-analytics__rag hub-analytics__rag--' + cleanupRag(rcu.openTotal, cuGoal) + '">' + rcu.openTotal + '</span>'
          : String(rcu.openTotal);
        html += '<tr><td>' + esc(code) + '</td>' +
          '<td>' + (r.leads.total || 0) + '</td>' +
          '<td>' + fmtDur(r.firstResponse.avgBizMs) + '</td>' +
          '<td>' + ragBadge(r.firstResponse.withinGoalPct) + '</td>' +
          '<td>' + (r.leads.noResponse || 0) + '</td>' +
          '<td>' + ragBadge(r.offHold.withinGoalPct) + '</td>' +
          '<td>' + openCell + '</td>' +
          '<td>' + (r.quotes ? fmtUsd(r.quotes.window.quotedUsd) : '—') + '</td>' +
          '<td>' + (r.quotes && r.quotes.pace && r.quotes.pace.paceRatio != null
            ? ragBadge(Math.round(r.quotes.pace.paceRatio * 100)) : '—') + '</td>' +
          '<td>' + (r.sales ? fmtUsd(r.sales.window.soldUsd) : '—') + '</td>' +
          '<td>' + (a.call || 0) + '</td>' +
          '<td>' + emailCount(a) + '</td>' +
          '<td>' + (a.text || 0) + '</td>' +
          '<td>' + (a.dropin || 0) + '</td></tr>';
      });
      html += '</tbody></table></div>';
      if (d.unassignedLeads) {
        html += '<p class="hub-analytics__note">' + d.unassignedLeads + ' unassigned lead' +
          (d.unassignedLeads === 1 ? '' : 's') + ' in range (not credited to any rep).</p>';
      }
      html += '</div>'; // close TEAM master card
    }

    // Footer: just the window range (reconstruction disclaimer + DD link
    // both dropped — Ryan 2026-06-10).
    html += '<div class="hub-analytics__foot">' +
      '<p class="hub-analytics__note">' + esc(d.window.from) + ' → ' + esc(d.window.to) + '</p></div>';

    root.innerHTML = html;
    bindControls();
    if (trendHasData) {
      if (isAC) {
        // avg per business day — same denominator as the card verdict
        var acBizDays = weekdaysBetween(d.window.from, d.window.to);
        var acActTotal = (act.call || 0) + emailCount(act) + (act.text || 0) + (act.dropin || 0) + (act.meeting || 0);
        buildActivityChart(activeTrend, actGoalDay, acBizDays > 0 ? acActTotal / acBizDays : null);
      } else if (isCU) {
        // Window mean of the daily open-count — keeps a freak spike/dip day
        // in perspective against the rep's normal level.
        var cuPts = (cu.trend && cu.trend.points) || [];
        var cuAvg = cuPts.length
          ? cuPts.reduce(function (s2, p) { return s2 + (p.count || 0); }, 0) / cuPts.length
          : null;
        buildTrendChart(cu.trend, cuGoal, cuAvg, null, 'count');
      } else {
        buildTrendChart(
          activeTrend,
          isFR ? (goals.firstResponseGoalMs || 2 * 3600000) : (goals.offHoldGoalMs || 8 * 3600000),
          isFR ? fr.avgBizMs : oh.avgMs,
          isFR ? fmtDur : fmtDurH,
          null, null, null,
          isFR ? 'lead' : 'follow-up'
        );
      }
    }
    if (qd && hasQuotesChart) {
      if (qMetric === 'pace') {
        buildPaceChart(qd.pace);
      } else if (qMetric === 'qo') {
        buildQuotedChart(qoTrend, qd.pace || null);
      } else {
        // Verdict vs the ≤N backlog goal (gray line), window mean dashed.
        var blPts = blTrend.points;
        var blAvg = blPts.length
          ? blPts.reduce(function (s2, p) { return s2 + (p.count || 0); }, 0) / blPts.length
          : null;
        buildTrendChart(blTrend, blGoal, blAvg, null, 'count', 'hub-analytics-quotes');
      }
    }
    if (sd && hasSalesChart) {
      if (state.salesMetric === 'sm') {
        var smWinDays = Math.max(1, Math.round((Date.parse(d.window.to) - Date.parse(d.window.from)) / 86400000) + 1);
        buildSoldCumChart(sd.soldCum, qd && qd.pace, smWinDays);
      } else if (state.salesMetric === 'ad') {
        buildMarginChart(vel, coh, mGoal, mFloor);
      } else {
        buildOutcomeChart(coh);
      }
    }
    animateSection = 'all'; // next render animates everything unless a tab says otherwise
  }

  /* Pace burn-up (Phase 2 hero): cumulative quoted-$ vs the required-pace
     line for the calendar year. Verdict semantics INVERT vs the Effort
     charts — being ABOVE the required line is GREEN (ahead of pace). The
     required line is diagonal, so coloring is per-segment/point against the
     required value at the same week. Shares the section's UX kit: frosted
     tooltip, ping on the live end, custom legend, in-canvas padding. */
  function buildPaceChart(pace) {
    if (!window.Chart) return;
    var canvas = document.getElementById('hub-analytics-quotes');
    if (!canvas) return;
    var pts = pace.trend.points;
    var green = cssVar('--rq-success', '#34d399');
    var red = cssVar('--rq-error', '#f87171');
    var muted = cssVar('--rq-text-muted', '#8892a0');
    var text = cssVar('--rq-text', '#e0e0e0');
    var border = cssVar('--rq-border', '#1e2a42');
    var aheadAt = function (i) { return pts[i].actual != null && pts[i].actual >= pts[i].required; };
    var colorAt = function (i) { return aheadAt(i) ? green : red; };
    // Full-year axis: actual is null after today, so the line stops at the
    // latest real week. (A dotted projection was tried and removed — Ryan
    // 2026-06-10: overkill; the Projected Finish stat carries that story.)
    var lastIdx = -1;
    for (var li = pts.length - 1; li >= 0; li--) {
      if (pts[li].actual != null) { lastIdx = li; break; }
    }
    var onAnimDone = null;
    var chart = new window.Chart(canvas, {
      type: 'line',
      data: {
        labels: pts.map(function (p) { return shortDate(p.t); }),
        datasets: [
          {
            label: 'Quoted (cumulative)',
            data: pts.map(function (p) { return p.actual; }),
            borderColor: green,
            backgroundColor: green,
            borderWidth: 3,
            tension: 0.3,
            clip: false,
            pointRadius: 2.5,
            pointHoverRadius: 5,
            segment: {
              borderColor: function (c) {
                return (aheadAt(c.p0DataIndex) && aheadAt(c.p1DataIndex)) ? green : red;
              },
            },
            pointBackgroundColor: function (c) { return colorAt(c.dataIndex); },
            pointBorderColor: function (c) { return colorAt(c.dataIndex); },
          },
          {
            label: 'YOUR GOAL',
            data: pts.map(function (p) { return p.required; }),
            borderColor: muted,
            backgroundColor: muted,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHitRadius: 0,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: animDuration('quotes'),
          onComplete: function () { if (onAnimDone) onAnimDone(); },
        },
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { bottom: 30 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            filter: function (item) { return item.datasetIndex === 0; },
            external: function (context) {
              var tt = context.tooltip;
              var wrap = canvas.parentElement;
              if (!wrap) return;
              var tip = ensureTip(wrap);
              var dp = (tt.dataPoints || []).filter(function (i) { return i.datasetIndex === 0; })[0];
              if (!tt.opacity || !dp || pts[dp.dataIndex].actual == null) { tip.style.opacity = '0'; return; }
              var p = pts[dp.dataIndex];
              tip.innerHTML = '<div class="hub-analytics__tip-date">' + niceDate(p.t, 'week') + '</div>' +
                '<div class="hub-analytics__tip-val" style="color:' + colorAt(dp.dataIndex) + '">' + fmtUsd(p.actual) + ' quoted</div>' +
                '<div class="hub-analytics__tip-date">' + fmtUsd(p.required) + ' needed</div>';
              var ex = dp.element ? dp.element.x : tt.caretX;
              var ey = dp.element ? dp.element.y : tt.caretY;
              var lx = canvas.offsetLeft + ex;
              var maxL = wrap.clientWidth - 56;
              if (lx < 56) lx = 56;
              if (lx > maxL) lx = maxL;
              tip.style.left = lx + 'px';
              tip.style.top = (canvas.offsetTop + ey) + 'px';
              tip.style.transform = ey < 80 ? 'translate(-50%, 14px)' : 'translate(-50%, calc(-100% - 14px))';
              tip.style.opacity = '1';
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: muted, maxTicksLimit: 12, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
          y: {
            beginAtZero: true,
            grid: { color: border },
            ticks: {
              color: muted,
              font: { family: 'Montserrat, sans-serif', size: 11 },
              callback: function (v) { return fmtUsd(v); },
            },
          },
        },
      },
    });
    charts.push(chart);
    // custom legend (same kit as the trend charts)
    (function () {
      var wrapL = canvas.parentElement;
      if (!wrapL) return;
      var lg = document.createElement('div');
      lg.className = 'hub-analytics__legend';
      var line = function (color) {
        return '<svg width="26" height="14" viewBox="0 0 26 14" aria-hidden="true"><line x1="1" y1="7" x2="25" y2="7" stroke="' + color + '" stroke-width="2"/></svg>';
      };
      // Swatch carries the line's LIVE verdict (ahead/behind at the latest
      // real week — same color the ping shows), not a fixed green.
      lg.innerHTML = '<span class="hub-analytics__legend-item">' + line(colorAt(lastIdx >= 0 ? lastIdx : 0)) + 'Quoted (cumulative)</span>' +
        '<span class="hub-analytics__legend-item">' + line(muted) + 'YOUR GOAL</span>';
      wrapL.appendChild(lg);
    })();
    // ping on the live end (the latest REAL week — actual is null beyond now)
    if (lastIdx >= 0) {
      var wrapP = canvas.parentElement;
      var ping = document.createElement('div');
      ping.className = 'hub-analytics__ping';
      ping.style.borderColor = colorAt(lastIdx);
      ping.style.display = 'none';
      wrapP.appendChild(ping);
      var positionPing = function () {
        var x = chart.scales.x.getPixelForValue(lastIdx);
        var y = chart.scales.y.getPixelForValue(pts[lastIdx].actual);
        if (!isFinite(x) || !isFinite(y)) { ping.style.display = 'none'; return; }
        ping.style.display = '';
        ping.style.left = (canvas.offsetLeft + x) + 'px';
        ping.style.top = (canvas.offsetTop + y) + 'px';
      };
      onAnimDone = positionPing;
      chart.options.onResize = function () { requestAnimationFrame(positionPing); };
    }
  }

  /* Activity volume (Effort 'Activity' tab, admin-only) — stacked daily/
     weekly bars, one segment per activity type. Bars beat lines here: counts
     are small and zero-heavy, and stacking shows volume + mix at once.
     Server folds Outlook outbound into 'email' per bucket. */
  function buildActivityChart(trend, goalPerDay, avgPerDay) {
    if (!window.Chart) return;
    var canvas = document.getElementById('hub-analytics-trend');
    if (!canvas) return;
    var pts = trend.points;
    var muted = cssVar('--rq-text-muted', '#8892a0');
    var border = cssVar('--rq-border', '#1e2a42');
    var blue = cssVar('--rq-blue', '#5ba8f7');
    var green = cssVar('--rq-success', '#34d399');
    var red = cssVar('--rq-error', '#f87171');
    var isWeekly = trend.granularity === 'week';
    // Gray goal line, same kit as every other chart. Weekly buckets carry
    // 5 business days of goal.
    var goalY = goalPerDay > 0 ? goalPerDay * (isWeekly ? 5 : 1) : null;
    // Chart palette: brand vars where they exist + two fixed series colors
    // (purple/teal — no portal var covers a 4th/5th accent).
    var SERIES = [
      { key: 'call', label: 'Calls', color: cssVar('--rq-blue', '#5ba8f7') },
      { key: 'email', label: 'Emails', color: cssVar('--rq-success', '#34d399') },
      { key: 'text', label: 'Texts', color: cssVar('--rq-changed-border', '#F59E0B') },
      { key: 'dropin', label: 'Drop-ins', color: '#c084fc' },
      { key: 'meeting', label: 'Meetings', color: '#2dd4bf' },
    ];
    var totalAt = function (p) { return p.call + p.email + p.text + p.dropin + p.meeting; };
    // Hovered bar gets visibly brighter (all segments at that x light up
    // together in index mode) — "that's the bar I'm on" (Ryan 2026-06-10).
    var lighten = function (hex, amt) {
      var m = String(hex).trim().match(/^#([0-9a-f]{6})$/i);
      if (!m) return hex;
      var n = parseInt(m[1], 16);
      var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      r = Math.round(r + (255 - r) * amt);
      g = Math.round(g + (255 - g) * amt);
      b = Math.round(b + (255 - b) * amt);
      return 'rgb(' + r + ',' + g + ',' + b + ')';
    };
    /* VERDICT bars v3 (Ryan 2026-06-11): coverage coloring RETIRED with the
       Deals Touched stat (Money Moves owns anti-neglect, by name). Bar
       HEIGHT = activity volume, bar COLOR = volume vs the daily/weekly
       goal. v3.1 (Ryan 2026-06-12): weekend neutral-blue retired too —
       every visible bar is red or green. Zero-activity weekends have no
       bar, so this only judges days the rep actually worked. */
    var totals = pts.map(totalAt);
    var verdictColor = function (i) {
      if (goalY == null) return blue;
      return totals[i] >= goalY ? green : red;
    };
    var datasets = [{
      label: 'Activities',
      data: totals,
      backgroundColor: function (c) { return verdictColor(c.dataIndex); },
      hoverBackgroundColor: function (c) { return lighten(verdictColor(c.dataIndex), 0.35); },
      maxBarThickness: 26,
      borderWidth: 0,
    }];
    // Dashed YOUR AVERAGE at the window's per-business-day mean (×5 weekly,
    // like the goal). Verdict INVERTS vs the duration charts — more activity
    // is better, so at/over the goal = green.
    var avgY = avgPerDay != null ? Math.round(avgPerDay * (isWeekly ? 5 : 1) * 10) / 10 : null;
    var avgColor = avgY != null ? (goalY != null ? (avgY >= goalY ? green : red) : blue) : null;
    // Goal + average draw as a canvas plugin spanning the FULL chart width —
    // line datasets on a category axis stop half a bar-slot short of the
    // edges (Ryan 2026-06-10).
    // Reference lines slide up with the bars' entry animation (Ryan
    // 2026-06-10) — the plugin eases their y from the chart floor using the
    // animation's progress (easeOutQuart, Chart.js's default curve).
    var lineProg = animDuration('effort') === 0 ? 1 : 0;
    var refLines = {
      id: 'haActivityRefLines',
      afterDatasetsDraw: function (c) {
        var area = c.chartArea;
        var yS = c.scales.y;
        if (!area || !yS) return;
        var ease = 1 - Math.pow(1 - Math.max(0, Math.min(1, lineProg)), 4);
        var drawLine = function (val, color, dash) {
          if (val == null) return;
          var py = yS.getPixelForValue(val);
          if (!isFinite(py) || py < area.top - 1 || py > area.bottom + 1) return;
          py = area.bottom - (area.bottom - py) * ease;
          c.ctx.save();
          c.ctx.strokeStyle = color;
          c.ctx.lineWidth = 2;
          c.ctx.setLineDash(dash || []);
          c.ctx.beginPath();
          c.ctx.moveTo(area.left, py);
          c.ctx.lineTo(area.right, py);
          c.ctx.stroke();
          c.ctx.restore();
        };
        drawLine(avgY, avgColor, [6, 4]);
        drawLine(goalY, muted, []);
      },
    };
    // Keep the reference lines on-chart even when every bar sits under them.
    var yMaxHint = Math.max(goalY || 0, avgY || 0);
    var chart = new window.Chart(canvas, {
      type: 'bar',
      plugins: [refLines],
      data: {
        labels: pts.map(function (p) { return shortDate(p.t); }),
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: animDuration('effort'),
          // Track ONLY the entry animation — hover transitions also fire
          // these callbacks and were replaying the line slide-up on mouseover.
          onProgress: function (a) { if (lineProg < 1 && a.numSteps) lineProg = a.currentStep / a.numSteps; },
          onComplete: function () { lineProg = 1; },
        },
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { bottom: 30 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: function (context) {
              var tt = context.tooltip;
              var wrap = canvas.parentElement;
              if (!wrap) return;
              var tip = ensureTip(wrap);
              // Bars only — the goal-line dataset must not anchor the tip.
              var barPoints = (tt.dataPoints || []).filter(function (i) { return i.datasetIndex === 0; });
              var dp = barPoints[0];
              if (!tt.opacity || !dp || totalAt(pts[dp.dataIndex]) === 0) { tip.style.opacity = '0'; return; }
              var p = pts[dp.dataIndex];
              var rows = '';
              SERIES.forEach(function (s2) {
                if (p[s2.key] > 0) {
                  rows += '<div class="hub-analytics__tip-date"><span style="color:' + s2.color + '">●</span> ' +
                    p[s2.key] + ' ' + s2.label.toLowerCase() + '</div>';
                }
              });
              // Deals-touched stays as tooltip trivia; the coverage ratio/%
              // retired with the Deals Touched stat (Ryan 2026-06-11).
              var covRow = '';
              if (p.deals > 0) {
                covRow = '<div class="hub-analytics__tip-date">' + p.deals + ' deal' + (p.deals === 1 ? '' : 's') + ' touched</div>';
              }
              tip.innerHTML = '<div class="hub-analytics__tip-date">' + niceDate(p.t, trend.granularity) + '</div>' +
                '<div class="hub-analytics__tip-val">' + totalAt(p) + ' activit' + (totalAt(p) === 1 ? 'y' : 'ies') +
                (p.open == null && p.deals > 0 ? ' across ' + p.deals + ' deal' + (p.deals === 1 ? '' : 's') : '') + '</div>' +
                covRow + rows;
              // Centered on the bar, ALWAYS above the stack's top (never
              // covering the bar — overlapping the tabs above is fine; the
              // tip z-indexes over them). Stack top = min segment y.
              var bx = dp.element ? dp.element.x : tt.caretX;
              var topY = Infinity;
              barPoints.forEach(function (i) {
                if (i.element && isFinite(i.element.y) && i.element.y < topY) topY = i.element.y;
              });
              if (!isFinite(topY)) topY = tt.caretY;
              var lx = canvas.offsetLeft + bx;
              var maxL = wrap.clientWidth - 56;
              if (lx < 56) lx = 56;
              if (lx > maxL) lx = maxL;
              tip.style.left = lx + 'px';
              tip.style.top = (canvas.offsetTop + topY) + 'px';
              tip.style.transform = 'translate(-50%, calc(-100% - 10px))';
              tip.style.opacity = '1';
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: muted, autoSkip: false, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
          y: {
            beginAtZero: true,
            suggestedMax: yMaxHint > 0 ? Math.ceil(yMaxHint * 1.1) : undefined,
            grid: { color: border },
            ticks: { color: muted, precision: 0, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
        },
      },
    });
    charts.push(chart);
    // Custom legend — square swatches, one per activity type.
    (function () {
      var wrapL = canvas.parentElement;
      if (!wrapL) return;
      var lg = document.createElement('div');
      lg.className = 'hub-analytics__legend';
      var sq = function (color) {
        return '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect width="10" height="10" rx="2" fill="' + color + '"/></svg>';
      };
      var dashLine = function (color, dashed) {
        return '<svg width="26" height="14" viewBox="0 0 26 14" aria-hidden="true"><line x1="1" y1="7" x2="25" y2="7" stroke="' + color + '" stroke-width="2"' +
          (dashed ? ' stroke-dasharray="5,4"' : '') + '/></svg>';
      };
      var lgHtml;
      // Coverage legend retired with the Deals Touched stat (Ryan
      // 2026-06-11) — bars are volume-vs-goal verdicts again.
      if (goalY != null) {
        lgHtml = '<span class="hub-analytics__legend-item">' + sq(green) + 'Goal Met</span>' +
          '<span class="hub-analytics__legend-item">' + sq(red) + 'Under Goal</span>';
      } else {
        lgHtml = '<span class="hub-analytics__legend-item">' + sq(blue) + 'Activities</span>';
      }
      if (avgColor) {
        lgHtml += '<span class="hub-analytics__legend-item">' + dashLine(avgColor, true) + 'YOUR AVERAGE</span>';
      }
      if (goalY != null) {
        // 'YOUR GOAL' (Ryan 2026-06-11) — aligned with the goal-line naming
        // on the other graphs; the legend's AVERAGE line already carries the
        // "touches" qualifier.
        lgHtml += '<span class="hub-analytics__legend-item">' + dashLine(muted, false) + 'YOUR GOAL</span>';
      }
      lg.innerHTML = lgHtml;
      wrapL.appendChild(lg);
    })();
  }

  /* Quote Output tab — quoted-$ per day/week as verdict bars (green at/over
     the required-pace share for that bucket, red under; blue when no goal),
     with the dashed YOUR AVERAGE + gray PACE/BUCKET line drawn full-width
     via the same canvas-plugin trick as the activity chart. Quoting is
     lumpy, so the average line is what keeps zero-days in perspective. */
  function buildQuotedChart(trend, pace) {
    if (!window.Chart) return;
    var canvas = document.getElementById('hub-analytics-quotes');
    if (!canvas) return;
    var pts = trend.points;
    var muted = cssVar('--rq-text-muted', '#8892a0');
    var border = cssVar('--rq-border', '#1e2a42');
    var blue = cssVar('--rq-blue', '#5ba8f7');
    var green = cssVar('--rq-success', '#34d399');
    var red = cssVar('--rq-error', '#f87171');
    var isWeekly = trend.granularity === 'week';
    // Required quoted-$ per bucket = the year's required total spread evenly.
    var goalY = pace && pace.requiredYear > 0
      ? Math.round(pace.requiredYear / 365 * (isWeekly ? 7 : 1))
      : null;
    var vals = pts.map(function (p) { return p.usd; });
    var avgY = pts.length
      ? Math.round(vals.reduce(function (s2, v) { return s2 + v; }, 0) / pts.length)
      : null;
    var avgColor = avgY != null ? (goalY != null ? (avgY >= goalY ? green : red) : blue) : null;
    var verdictColor = function (v) {
      return goalY == null ? blue : (v >= goalY ? green : red);
    };
    var lighten = function (hex, amt) {
      var m = String(hex).trim().match(/^#([0-9a-f]{6})$/i);
      if (!m) return hex;
      var n = parseInt(m[1], 16);
      var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      r = Math.round(r + (255 - r) * amt);
      g = Math.round(g + (255 - g) * amt);
      b = Math.round(b + (255 - b) * amt);
      return 'rgb(' + r + ',' + g + ',' + b + ')';
    };
    // Reference lines slide up with the bars (same eased-progress trick as
    // the activity chart).
    var lineProg = animDuration('quotes') === 0 ? 1 : 0;
    var refLines = {
      id: 'haQuotedRefLines',
      afterDatasetsDraw: function (c) {
        var area = c.chartArea;
        var yS = c.scales.y;
        if (!area || !yS) return;
        var ease = 1 - Math.pow(1 - Math.max(0, Math.min(1, lineProg)), 4);
        var drawLine = function (val, color, dash) {
          if (val == null) return;
          var py = yS.getPixelForValue(val);
          if (!isFinite(py) || py < area.top - 1 || py > area.bottom + 1) return;
          py = area.bottom - (area.bottom - py) * ease;
          c.ctx.save();
          c.ctx.strokeStyle = color;
          c.ctx.lineWidth = 2;
          c.ctx.setLineDash(dash || []);
          c.ctx.beginPath();
          c.ctx.moveTo(area.left, py);
          c.ctx.lineTo(area.right, py);
          c.ctx.stroke();
          c.ctx.restore();
        };
        drawLine(avgY, avgColor, [6, 4]);
        drawLine(goalY, muted, []);
      },
    };
    var yMaxHint = Math.max(goalY || 0, avgY || 0);
    var chart = new window.Chart(canvas, {
      type: 'bar',
      plugins: [refLines],
      data: {
        labels: pts.map(function (p) { return shortDate(p.t); }),
        datasets: [{
          label: 'Quoted $',
          data: vals,
          backgroundColor: function (c) { return verdictColor(vals[c.dataIndex]); },
          hoverBackgroundColor: function (c) { return lighten(verdictColor(vals[c.dataIndex]), 0.35); },
          maxBarThickness: 26,
          borderWidth: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: animDuration('quotes'),
          // Track ONLY the entry animation — hover transitions also fire
          // these callbacks and were replaying the line slide-up on mouseover.
          onProgress: function (a) { if (lineProg < 1 && a.numSteps) lineProg = a.currentStep / a.numSteps; },
          onComplete: function () { lineProg = 1; },
        },
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { bottom: 30 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: function (context) {
              var tt = context.tooltip;
              var wrap = canvas.parentElement;
              if (!wrap) return;
              var tip = ensureTip(wrap);
              var barPoints = (tt.dataPoints || []).filter(function (i) { return i.datasetIndex === 0; });
              var dp = barPoints[0];
              if (!tt.opacity || !dp || vals[dp.dataIndex] === 0) { tip.style.opacity = '0'; return; }
              var p = pts[dp.dataIndex];
              tip.innerHTML = '<div class="hub-analytics__tip-date">' + niceDate(p.t, trend.granularity) + '</div>' +
                '<div class="hub-analytics__tip-val" style="color:' + verdictColor(p.usd) + '">' + fmtUsd(p.usd) + ' quoted</div>' +
                '<div class="hub-analytics__tip-date">' + p.n + ' quote' + (p.n === 1 ? '' : 's') + ' sent</div>' +
                (goalY != null ? '<div class="hub-analytics__tip-date">' + fmtUsd(goalY) + ' needed</div>' : '');
              var bx = dp.element ? dp.element.x : tt.caretX;
              var topY = dp.element && isFinite(dp.element.y) ? dp.element.y : tt.caretY;
              var lx = canvas.offsetLeft + bx;
              var maxL = wrap.clientWidth - 56;
              if (lx < 56) lx = 56;
              if (lx > maxL) lx = maxL;
              tip.style.left = lx + 'px';
              tip.style.top = (canvas.offsetTop + topY) + 'px';
              tip.style.transform = 'translate(-50%, calc(-100% - 10px))';
              tip.style.opacity = '1';
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: muted, autoSkip: false, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
          y: {
            beginAtZero: true,
            suggestedMax: yMaxHint > 0 ? Math.ceil(yMaxHint * 1.1) : undefined,
            grid: { color: border },
            ticks: {
              color: muted,
              font: { family: 'Montserrat, sans-serif', size: 11 },
              callback: function (v) { return fmtUsd(v); },
            },
          },
        },
      },
    });
    charts.push(chart);
    (function () {
      var wrapL = canvas.parentElement;
      if (!wrapL) return;
      var lg = document.createElement('div');
      lg.className = 'hub-analytics__legend';
      var sq = function (color) {
        return '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect width="10" height="10" rx="2" fill="' + color + '"/></svg>';
      };
      var dashLine = function (color, dashed) {
        return '<svg width="26" height="14" viewBox="0 0 26 14" aria-hidden="true"><line x1="1" y1="7" x2="25" y2="7" stroke="' + color + '" stroke-width="2"' +
          (dashed ? ' stroke-dasharray="5,4"' : '') + '/></svg>';
      };
      var lgHtml;
      if (goalY != null) {
        lgHtml = '<span class="hub-analytics__legend-item">' + sq(green) + 'On Pace</span>' +
          '<span class="hub-analytics__legend-item">' + sq(red) + 'Under Pace</span>';
      } else {
        lgHtml = '<span class="hub-analytics__legend-item">' + sq(blue) + 'Quoted $</span>';
      }
      if (avgColor) lgHtml += '<span class="hub-analytics__legend-item">' + dashLine(avgColor, true) + 'YOUR AVERAGE</span>';
      if (goalY != null) {
        // 'YOUR GOAL' (Ryan 2026-06-11) — aligned with the other graphs.
        lgHtml += '<span class="hub-analytics__legend-item">' + dashLine(muted, false) + 'YOUR GOAL</span>';
      }
      lg.innerHTML = lgHtml;
      wrapL.appendChild(lg);
    })();
  }

  /* GP Margin tab (Ryan 2026-06-10) — one bar per close date: the day's
     blended GP margin %. Green at/above the 25% goal, amber in the
     20-25% no-permission zone, red below the 20% floor. Solid gray GOAL
     line + dashed gray FLOOR line. Days-to-close survives as tooltip
     trivia per deal. */
  function buildMarginChart(vel, coh, mGoal, mFloor) {
    if (!window.Chart) return;
    var canvas = document.getElementById('hub-analytics-sales');
    if (!canvas) return;
    var muted = cssVar('--rq-text-muted', '#8892a0');
    var border = cssVar('--rq-border', '#1e2a42');
    var green = cssVar('--rq-success', '#34d399');
    var amber = cssVar('--rq-changed-border', '#F59E0B');
    var red = cssVar('--rq-error', '#f87171');
    // Gap-free window axis (cohort buckets); same-day wins share a bar at
    // their blended margin with each deal listed in the tooltip.
    var axisKeys = (coh && coh.points || []).map(function (p) { return p.t; });
    var byBucket = {};
    vel.points.forEach(function (p) {
      (byBucket[p.t] || (byBucket[p.t] = [])).push(p);
    });
    var pts = axisKeys.map(function (k) { return { t: k, deals: byBucket[k] || [] }; });
    var vals = pts.map(function (p) {
      var usd = p.deals.reduce(function (s2, d2) { return s2 + d2.usd; }, 0);
      var gp = p.deals.reduce(function (s2, d2) { return s2 + (d2.gp || 0); }, 0);
      return usd > 0 ? Math.round((gp / usd) * 1000) / 10 : 0;
    });
    var verdictColor = function (v) {
      return v >= mGoal ? green : (v >= mFloor ? amber : red);
    };
    var lighten = function (hex, amt) {
      var m = String(hex).trim().match(/^#([0-9a-f]{6})$/i);
      if (!m) return hex;
      var n = parseInt(m[1], 16);
      var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      r = Math.round(r + (255 - r) * amt);
      g = Math.round(g + (255 - g) * amt);
      b = Math.round(b + (255 - b) * amt);
      return 'rgb(' + r + ',' + g + ',' + b + ')';
    };
    var lineProg = animDuration('sales') === 0 ? 1 : 0;
    var refLines = {
      id: 'haMarginRefLines',
      afterDatasetsDraw: function (c) {
        var area = c.chartArea;
        var yS = c.scales.y;
        if (!area || !yS) return;
        var ease = 1 - Math.pow(1 - Math.max(0, Math.min(1, lineProg)), 4);
        var drawLine = function (val, dash) {
          if (val == null) return;
          var py = yS.getPixelForValue(val);
          if (!isFinite(py) || py < area.top - 1 || py > area.bottom + 1) return;
          py = area.bottom - (area.bottom - py) * ease;
          c.ctx.save();
          c.ctx.strokeStyle = muted;
          c.ctx.lineWidth = 2;
          c.ctx.setLineDash(dash || []);
          c.ctx.beginPath();
          c.ctx.moveTo(area.left, py);
          c.ctx.lineTo(area.right, py);
          c.ctx.stroke();
          c.ctx.restore();
        };
        drawLine(mGoal, []); // floor line removed (Ryan 2026-06-10) — amber bars carry the band
      },
    };
    var chart = new window.Chart(canvas, {
      type: 'bar',
      plugins: [refLines],
      data: {
        labels: pts.map(function (p) { return shortDate(p.t); }),
        datasets: [{
          label: 'GP Margin %',
          data: vals,
          backgroundColor: function (c) { return verdictColor(vals[c.dataIndex]); },
          hoverBackgroundColor: function (c) { return lighten(verdictColor(vals[c.dataIndex]), 0.35); },
          maxBarThickness: 30,
          borderWidth: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: animDuration('sales'),
          onProgress: function (a) { if (lineProg < 1 && a.numSteps) lineProg = a.currentStep / a.numSteps; },
          onComplete: function () { lineProg = 1; },
        },
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { bottom: 30 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: function (context) {
              var tt = context.tooltip;
              var wrap = canvas.parentElement;
              if (!wrap) return;
              var tip = ensureTip(wrap);
              var dp = (tt.dataPoints || []).filter(function (i) { return i.datasetIndex === 0; })[0];
              if (!tt.opacity || !dp || !pts[dp.dataIndex].deals.length) { tip.style.opacity = '0'; return; }
              var p = pts[dp.dataIndex];
              var rows = p.deals.map(function (d2) {
                return '<div class="hub-analytics__tip-date">' + esc(d2.ref) +
                  (d2.customer ? ' &middot; ' + esc(d2.customer) : '') + ' &middot; ' + fmtUsd(d2.usd) +
                  (d2.marginPct != null ? ' &middot; ' + d2.marginPct + '%' : '') +
                  (d2.days != null ? ' &middot; ' + d2.days + 'd to Close' : '') + '</div>';
              }).join('');
              var v2 = vals[dp.dataIndex];
              tip.innerHTML = '<div class="hub-analytics__tip-date">Won ' + niceDate(p.t, coh ? coh.granularity : 'day') + '</div>' +
                '<div class="hub-analytics__tip-val" style="color:' + verdictColor(v2) + '">' + v2 + '% GP Margin' +
                (p.deals.length > 1 ? ' (Blended, ' + p.deals.length + ' Deals)' : '') + '</div>' + rows;
              var bx = dp.element ? dp.element.x : tt.caretX;
              var topY = dp.element && isFinite(dp.element.y) ? dp.element.y : tt.caretY;
              var lx = canvas.offsetLeft + bx;
              var maxL = wrap.clientWidth - 56;
              if (lx < 56) lx = 56;
              if (lx > maxL) lx = maxL;
              tip.style.left = lx + 'px';
              tip.style.top = (canvas.offsetTop + topY) + 'px';
              tip.style.transform = 'translate(-50%, calc(-100% - 10px))';
              tip.style.opacity = '1';
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: muted, autoSkip: false, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
          y: {
            beginAtZero: true,
            suggestedMax: Math.ceil(mGoal * 1.4),
            grid: { color: border },
            ticks: {
              color: muted,
              font: { family: 'Montserrat, sans-serif', size: 11 },
              callback: function (v) { return v + '%'; },
            },
          },
        },
      },
    });
    charts.push(chart);
    (function () {
      var wrapL = canvas.parentElement;
      if (!wrapL) return;
      var lg = document.createElement('div');
      lg.className = 'hub-analytics__legend';
      var sq = function (color) {
        return '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect width="10" height="10" rx="2" fill="' + color + '"/></svg>';
      };
      var lineSw = function (dashed) {
        return '<svg width="26" height="14" viewBox="0 0 26 14" aria-hidden="true"><line x1="1" y1="7" x2="25" y2="7" stroke="' + muted + '" stroke-width="2"' +
          (dashed ? ' stroke-dasharray="5,4"' : '') + '/></svg>';
      };
      lg.innerHTML = '<span class="hub-analytics__legend-item">' + sq(green) + '≥' + mGoal + '%</span>' +
        '<span class="hub-analytics__legend-item">' + sq(amber) + mFloor + '–' + mGoal + '%</span>' +
        '<span class="hub-analytics__legend-item">' + sq(red) + '<' + mFloor + '%</span>' +
        '<span class="hub-analytics__legend-item">' + lineSw(false) + 'GOAL</span>';
      wrapL.appendChild(lg);
    })();
  }

  /* Sold & GP tab (Ryan 2026-06-10 redesign) — cumulative sold-$ STEP chart
     across the SELECTED TIMELINE WINDOW: the line steps up at each sale,
     red while below the YOUR GOAL diagonal (the window's prorated share of
     annualTarget — the annual mountain lives on the Quotes pace chart) and
     green where it touches/passes it. Cumulative GP rides along as a thinner
     DASHED line inheriting SOLD's verdict color (sold red → GP red; sold
     green → GP green). No goal → neutral blue. */
  function buildSoldCumChart(soldCum, pace, windowDays) {
    if (!window.Chart) return;
    var canvas = document.getElementById('hub-analytics-sales');
    if (!canvas) return;
    var pts = soldCum.points;
    var green = cssVar('--rq-success', '#34d399');
    var red = cssVar('--rq-error', '#f87171');
    var blue = cssVar('--rq-blue', '#5ba8f7');
    var muted = cssVar('--rq-text-muted', '#8892a0');
    var border = cssVar('--rq-border', '#1e2a42');
    var target = pace && pace.annualTarget > 0 && windowDays > 0
      ? pace.annualTarget * windowDays / 365
      : null;
    var goalAt = function (i) { return target != null ? target * ((i + 1) / pts.length) : null; };
    var aheadAt = function (i) {
      return target == null ? true : (pts[i].actual != null && pts[i].actual >= goalAt(i));
    };
    var colorAt = function (i) {
      if (target == null) return blue;
      return aheadAt(i) ? green : red;
    };
    var lastIdx = -1;
    for (var li = pts.length - 1; li >= 0; li--) {
      if (pts[li].actual != null) { lastIdx = li; break; }
    }
    var segColor = function (c) {
      if (target == null) return blue;
      return (aheadAt(c.p0DataIndex) && aheadAt(c.p1DataIndex)) ? green : red;
    };
    var datasets = [
      {
        label: 'Sold (cumulative)',
        data: pts.map(function (p) { return p.actual; }),
        borderColor: target == null ? blue : green,
        backgroundColor: target == null ? blue : green,
        borderWidth: 3,
        stepped: true,
        clip: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        segment: { borderColor: segColor },
        pointBackgroundColor: function (c) { return colorAt(c.dataIndex); },
        pointBorderColor: function (c) { return colorAt(c.dataIndex); },
      },
      {
        label: 'GP (cumulative)',
        data: pts.map(function (p) { return p.gp; }),
        borderColor: target == null ? blue : green,
        backgroundColor: target == null ? blue : green,
        borderWidth: 2,
        borderDash: [6, 4],
        stepped: true,
        clip: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        segment: { borderColor: segColor }, // GP inherits SOLD's verdict
      },
    ];
    if (target != null) {
      datasets.push({
        label: 'YOUR GOAL',
        data: pts.map(function (p, i) { return Math.round(goalAt(i)); }),
        borderColor: muted,
        backgroundColor: muted,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
        fill: false,
      });
    }
    var onAnimDone = null;
    var chart = new window.Chart(canvas, {
      type: 'line',
      data: { labels: pts.map(function (p) { return shortDate(p.t); }), datasets: datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: animDuration('sales'),
          onComplete: function () { if (onAnimDone) onAnimDone(); },
        },
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { bottom: 30 } },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            filter: function (item) { return item.datasetIndex === 0; },
            external: function (context) {
              var tt = context.tooltip;
              var wrap = canvas.parentElement;
              if (!wrap) return;
              var tip = ensureTip(wrap);
              var dp = (tt.dataPoints || []).filter(function (i) { return i.datasetIndex === 0; })[0];
              if (!tt.opacity || !dp || pts[dp.dataIndex].actual == null) { tip.style.opacity = '0'; return; }
              var p = pts[dp.dataIndex];
              tip.innerHTML = '<div class="hub-analytics__tip-date">' + niceDate(p.t, soldCum.granularity) + '</div>' +
                '<div class="hub-analytics__tip-val" style="color:' + colorAt(dp.dataIndex) + '">' + fmtUsd(p.actual) + ' Sold &middot; ' + fmtUsd(p.gp) + ' GP</div>' +
                (target != null ? '<div class="hub-analytics__tip-date">' + fmtUsd(goalAt(dp.dataIndex)) + ' Needed</div>' : '');
              var ex = dp.element ? dp.element.x : tt.caretX;
              var ey = dp.element ? dp.element.y : tt.caretY;
              var lx = canvas.offsetLeft + ex;
              var maxL = wrap.clientWidth - 56;
              if (lx < 56) lx = 56;
              if (lx > maxL) lx = maxL;
              tip.style.left = lx + 'px';
              tip.style.top = (canvas.offsetTop + ey) + 'px';
              tip.style.transform = ey < 80 ? 'translate(-50%, 14px)' : 'translate(-50%, calc(-100% - 14px))';
              tip.style.opacity = '1';
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: muted, maxTicksLimit: 12, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
          y: {
            beginAtZero: true,
            grid: { color: border },
            ticks: {
              color: muted,
              font: { family: 'Montserrat, sans-serif', size: 11 },
              callback: function (v) { return fmtUsd(v); },
            },
          },
        },
      },
    });
    charts.push(chart);
    (function () {
      var wrapL = canvas.parentElement;
      if (!wrapL) return;
      var lg = document.createElement('div');
      lg.className = 'hub-analytics__legend';
      var line = function (color, dashed) {
        return '<svg width="26" height="14" viewBox="0 0 26 14" aria-hidden="true"><line x1="1" y1="7" x2="25" y2="7" stroke="' + color + '" stroke-width="2"' +
          (dashed ? ' stroke-dasharray="5,4"' : '') + '/></svg>';
      };
      var liveColor = colorAt(lastIdx >= 0 ? lastIdx : 0);
      lg.innerHTML = '<span class="hub-analytics__legend-item">' + line(liveColor, false) + 'Sold (cumulative)</span>' +
        '<span class="hub-analytics__legend-item">' + line(liveColor, true) + 'GP (cumulative)</span>' +
        (target != null ? '<span class="hub-analytics__legend-item">' + line(muted, false) + 'YOUR GOAL</span>' : '');
      wrapL.appendChild(lg);
    })();
    // ping on the live end of the sold line
    if (lastIdx >= 0) {
      var wrapP = canvas.parentElement;
      var ping = document.createElement('div');
      ping.className = 'hub-analytics__ping';
      ping.style.borderColor = colorAt(lastIdx);
      ping.style.display = 'none';
      wrapP.appendChild(ping);
      var positionPing = function () {
        var x = chart.scales.x.getPixelForValue(lastIdx);
        var y = chart.scales.y.getPixelForValue(pts[lastIdx].actual);
        if (!isFinite(x) || !isFinite(y)) { ping.style.display = 'none'; return; }
        ping.style.display = '';
        ping.style.left = (canvas.offsetLeft + x) + 'px';
        ping.style.top = (canvas.offsetTop + y) + 'px';
      };
      onAnimDone = positionPing;
      chart.options.onResize = function () { requestAnimationFrame(positionPing); };
    }
  }

  /* Close Rate tab (Ryan 2026-06-10) — stacked outcome bars: one bar per
     arrival day/week, split into Won (green) / Lost (red) / Still Open
     (blue, could go either way). Makes the close-rate denominator literally
     visible — every deal that arrived is in the bar somewhere. */
  function buildOutcomeChart(coh) {
    if (!window.Chart) return;
    var canvas = document.getElementById('hub-analytics-sales');
    if (!canvas) return;
    var pts = coh.points;
    var muted = cssVar('--rq-text-muted', '#8892a0');
    var border = cssVar('--rq-border', '#1e2a42');
    var blue = cssVar('--rq-blue', '#5ba8f7');
    var green = cssVar('--rq-success', '#34d399');
    var red = cssVar('--rq-error', '#f87171');
    // 'Open at $0' = open, no quote sent, no value (hollow blue); 'Still
    // Open' = open with a quote/value in play (solid blue). Ryan 2026-06-10.
    var open0At = function (p) { return p.open0 || 0; };
    var openAt = function (p) { return Math.max(0, p.deals - p.won - p.lost - open0At(p)); };
    var alpha = function (hex, a) {
      var m = String(hex).trim().match(/^#([0-9a-f]{6})$/i);
      if (!m) return hex;
      var n = parseInt(m[1], 16);
      return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
    };
    var lighten = function (hex, amt) {
      var m = String(hex).trim().match(/^#([0-9a-f]{6})$/i);
      if (!m) return hex;
      var n = parseInt(m[1], 16);
      var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
      r = Math.round(r + (255 - r) * amt);
      g = Math.round(g + (255 - g) * amt);
      b = Math.round(b + (255 - b) * amt);
      return 'rgb(' + r + ',' + g + ',' + b + ')';
    };
    var SERIES = [
      { label: 'Won', color: green, get: function (p) { return p.won; } },
      { label: 'Lost', color: red, get: function (p) { return p.lost; } },
      { label: 'Open at $0', color: blue, hollow: true, get: open0At },
      { label: 'Still Open', color: blue, get: openAt },
    ];
    var chart = new window.Chart(canvas, {
      type: 'bar',
      data: {
        labels: pts.map(function (p) { return shortDate(p.t); }),
        datasets: SERIES.map(function (s2) {
          return {
            label: s2.label,
            data: pts.map(s2.get),
            backgroundColor: s2.hollow ? alpha(s2.color, 0.18) : s2.color,
            hoverBackgroundColor: s2.hollow ? alpha(s2.color, 0.35) : lighten(s2.color, 0.35),
            borderColor: s2.hollow ? s2.color : undefined,
            borderWidth: s2.hollow ? 1 : 0,
            stack: 'outcome',
            maxBarThickness: 26,
          };
        }),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: animDuration('sales') },
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { bottom: 30 } },
        plugins: {
          legend: { display: false },
          // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-07-01) — this chart is the deal-COUNT
          // breakdown under the $-weighted Win Rate headline; title makes that explicit.
          title: { display: true, text: 'Deal Outcomes (by count)', color: muted, font: { family: 'Montserrat, sans-serif', size: 11 }, padding: { bottom: 6 } },
          tooltip: {
            enabled: false,
            external: function (context) {
              var tt = context.tooltip;
              var wrap = canvas.parentElement;
              if (!wrap) return;
              var tip = ensureTip(wrap);
              var barPoints = (tt.dataPoints || []).filter(function (i) { return i.datasetIndex < SERIES.length; });
              var dp = barPoints[0];
              if (!tt.opacity || !dp || pts[dp.dataIndex].deals === 0) { tip.style.opacity = '0'; return; }
              var p = pts[dp.dataIndex];
              var rows = '';
              if (p.won > 0) rows += '<div class="hub-analytics__tip-date"><span style="color:' + green + '">●</span> ' + p.won + ' Won</div>';
              if (p.lost > 0) rows += '<div class="hub-analytics__tip-date"><span style="color:' + red + '">●</span> ' + p.lost + ' Lost</div>';
              if (open0At(p) > 0) rows += '<div class="hub-analytics__tip-date"><span style="color:' + blue + '">○</span> ' + open0At(p) + ' Open at $0</div>';
              if (openAt(p) > 0) rows += '<div class="hub-analytics__tip-date"><span style="color:' + blue + '">●</span> ' + openAt(p) + ' Still Open</div>';
              tip.innerHTML = '<div class="hub-analytics__tip-date">' + niceDate(p.t, coh.granularity) + '</div>' +
                '<div class="hub-analytics__tip-val">' + p.deals + ' Deal' + (p.deals === 1 ? '' : 's') + ' Arrived</div>' + rows;
              var bx = dp.element ? dp.element.x : tt.caretX;
              var topY = Infinity;
              barPoints.forEach(function (i) {
                if (i.element && isFinite(i.element.y) && i.element.y < topY) topY = i.element.y;
              });
              if (!isFinite(topY)) topY = tt.caretY;
              var lx = canvas.offsetLeft + bx;
              var maxL = wrap.clientWidth - 56;
              if (lx < 56) lx = 56;
              if (lx > maxL) lx = maxL;
              tip.style.left = lx + 'px';
              tip.style.top = (canvas.offsetTop + topY) + 'px';
              tip.style.transform = 'translate(-50%, calc(-100% - 10px))';
              tip.style.opacity = '1';
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { color: muted, autoSkip: false, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: border },
            ticks: { color: muted, precision: 0, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
        },
      },
    });
    charts.push(chart);
    (function () {
      var wrapL = canvas.parentElement;
      if (!wrapL) return;
      var lg = document.createElement('div');
      lg.className = 'hub-analytics__legend';
      lg.innerHTML = SERIES.map(function (s2) {
        var swatch = s2.hollow
          ? '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect x="0.5" y="0.5" width="9" height="9" rx="2" fill="' + alpha(s2.color, 0.18) + '" stroke="' + s2.color + '"/></svg>'
          : '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect width="10" height="10" rx="2" fill="' + s2.color + '"/></svg>';
        return '<span class="hub-analytics__legend-item">' + swatch + esc(s2.label) + '</span>';
      }).join('');
      wrapL.appendChild(lg);
    })();
  }

  function bindControls() {
    // Selects live in the page-head mount (outside root); query document so
    // both the mounted and the fallback-in-root locations bind.
    var rangeSel = document.querySelector('[data-ctl="range"]');
    if (rangeSel) {
      rangeSel.addEventListener('change', function () {
        if (state.range === rangeSel.value) return;
        state.range = rangeSel.value;
        load();
      });
    }
    var repSel = document.querySelector('[data-ctl="rep"]');
    if (repSel) {
      repSel.addEventListener('change', function () {
        if (state.rep === repSel.value) return;
        state.rep = repSel.value;
        render();
      });
    }
    var snapBtn = document.querySelector('[data-ctl="snapshot"]');
    if (snapBtn) snapBtn.addEventListener('click', openSnapshotModal);
    root.querySelectorAll('.hub-analytics__group-link').forEach(function (el) {
      el.addEventListener('click', function (e) { e.stopPropagation(); });
    });
    // WHY headers: open the research slide-out, don't switch the tab.
    root.querySelectorAll('[data-why]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.stopPropagation();
        openWhyPane(el.getAttribute('data-why'));
      });
    });
    root.querySelectorAll('[data-metric]').forEach(function (el) {
      function activate() {
        var k = el.getAttribute('data-metric');
        if (state.metric !== k) { state.metric = k; animateSection = 'effort'; render(); }
      }
      el.addEventListener('click', activate);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      });
    });
    root.querySelectorAll('[data-salesmetric]').forEach(function (el) {
      function activate() {
        var k = el.getAttribute('data-salesmetric');
        if (state.salesMetric !== k) { state.salesMetric = k; animateSection = 'sales'; render(); }
      }
      el.addEventListener('click', activate);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      });
    });
    root.querySelectorAll('[data-quotesmetric]').forEach(function (el) {
      function activate() {
        var k = el.getAttribute('data-quotesmetric');
        if (state.quotesMetric !== k) { state.quotesMetric = k; animateSection = 'quotes'; render(); }
      }
      el.addEventListener('click', activate);
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      });
    });
  }

  // Mon–Fri count between two YYYY-MM-DD dates, inclusive.
  function weekdaysBetween(fromStr, toStr) {
    var d2 = new Date(fromStr + 'T12:00:00');
    var end = new Date(toStr + 'T12:00:00');
    var n = 0;
    for (var i = 0; i < 800 && d2 <= end; i++) {
      var w = d2.getDay();
      if (w !== 0 && w !== 6) n++;
      d2.setDate(d2.getDate() + 1);
    }
    return n;
  }

  // 'YYYY-MM-DD' → true when Sat/Sun (noon-parse dodges tz rollover).
  // Shared by the Activity chart's bar verdicts AND the Deals Touched
  // stat's averaged verdict — the two must judge the same buckets.
  function isWeekendKey(t) {
    var w = new Date(t + 'T12:00:00').getDay();
    return w === 0 || w === 6;
  }

  // 'YYYY-MM-DD' → 'M/D' (weekly buckets are keyed by their Monday)
  function shortDate(key) {
    var p = String(key).split('-');
    return parseInt(p[1], 10) + '/' + parseInt(p[2], 10);
  }

  var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  function niceDate(key, granularity) {
    var p = String(key).split('-');
    var label = (MONTH_NAMES[parseInt(p[1], 10) - 1] || p[1]) + ' ' + parseInt(p[2], 10);
    if (granularity === 'week') return 'Week of ' + label;
    if (granularity === 'day') {
      // Day buckets lead with the weekday — 'Thu Jun 11' (Ryan 2026-06-11).
      // Noon-parse avoids any timezone date rollover on the bare key.
      var d = new Date(key + 'T12:00:00');
      var wd = DOW_NAMES[d.getDay()];
      if (wd) return wd + ' ' + label;
    }
    return label;
  }

  /* Frosted-glass hover popup — same UX as Marketing Ops' Daily Performance
     chart (.moph-chart__tip): translucent panel + backdrop blur, date line,
     bold series-colored value. Chart.js canvas tooltips can't blur, so this
     is an external HTML tooltip living inside the chart wrapper. */
  function ensureTip(wrap) {
    var tip = wrap.querySelector('.hub-analytics__tip');
    if (!tip) {
      tip = document.createElement('div');
      tip.className = 'hub-analytics__tip';
      tip.style.opacity = '0';
      wrap.appendChild(tip);
    }
    return tip;
  }

  /* mode: 'duration' (default — values are ms, y-axis hours) or 'count'
     (Friday Clean-Up — values are open-item counts, integer y-axis, no
     average line, Friday ticks highlighted). For duration: goalVal/avgVal
     are ms. For count: goalVal is a plain count. */
  // goalLabel overrides the gray line's legend text — the Backlog chart uses
  // the window AVERAGE as its reference (no goal is defined for backlog).
  // noun (duration mode): what one sample is — 'lead' (First Outreach) /
  // 'follow-up' (Off-Hold). Dots are PER-BUCKET MEANS and can hold several
  // samples; the tooltip discloses the count so eyeball-averaging the dots
  // isn't mistaken for the card's true per-sample mean (Ryan 2026-06-12 —
  // 5 dots averaged 8.4h while the card correctly said 12h).
  function buildTrendChart(trend, goalVal, avgVal, fmt, mode, canvasId, goalLabel, noun) {
    if (!window.Chart) return;
    var canvas = document.getElementById(canvasId || 'hub-analytics-trend');
    if (!canvas) return;
    fmt = fmt || fmtDur;
    var isCount = mode === 'count';
    var pts = trend.points;
    var blue = cssVar('--rq-blue', '#5ba8f7');
    var red = cssVar('--rq-error', '#f87171');
    var green = cssVar('--rq-success', '#34d399');
    var muted = cssVar('--rq-text-muted', '#8892a0');
    var text = cssVar('--rq-text', '#e0e0e0');
    var border = cssVar('--rq-border', '#1e2a42');
    var toH = function (ms) { return Math.round((ms / 3600000) * 100) / 100; };
    var getVal = isCount
      ? function (p) { return p.count == null ? null : p.count; }
      : function (p) { return p.avgMs == null ? null : toH(p.avgMs); };
    // goalVal null → no goal exists for this metric (e.g. Backlog) — the
    // line renders neutral brand-blue with no verdict and no gray line.
    var goalY = goalVal == null ? null : (isCount ? goalVal : toH(goalVal));
    var tipText = isCount
      ? function (p) { return p.count + ' open item' + (p.count === 1 ? '' : 's'); }
      : function (p) { return fmt(p.avgMs); };
    var seriesLabel = isCount
      ? 'Open items'
      : (trend.granularity === 'week' ? 'Weekly' : 'Daily') + ' average';
    // Flat LEAD-IN: when the first real value lands mid-range (e.g. the 6th),
    // extend it back to the left edge so the line spans the full axis instead
    // of starting abruptly (Ryan 2026-06-10). Synthetic lead-in points carry
    // no dot and no tooltip — rawVals keeps the truth.
    var rawVals = pts.map(getVal);
    var lastRealIdx = -1;
    for (var lri = rawVals.length - 1; lri >= 0; lri--) {
      if (rawVals[lri] != null) { lastRealIdx = lri; break; }
    }
    var firstRealIdx = rawVals.findIndex(function (v) { return v != null; });
    var dataVals = rawVals.slice();
    /* Data-aware lead-in (Ryan 2026-06-10): when real dots exist BEFORE the
       visible window (server sends trend.prev = latest off-screen event),
       the line enters the chart already on the trajectory from that point
       into the first visible dot — time-interpolated, so the curvature is
       the line's own. When the window's first dot is the first dot EVER
       (prev null), the line starts at that dot, no extension. */
    var prev = !isCount && trend.prev && trend.prev.avgMs != null ? trend.prev : null;
    if (prev && firstRealIdx > 0) {
      var prevV = toH(prev.avgMs);
      var firstV = rawVals[firstRealIdx];
      var firstMs = Date.parse(pts[firstRealIdx].t + 'T12:00:00');
      var span = firstMs - prev.ms;
      if (span > 0) {
        for (var fi = 0; fi < firstRealIdx; fi++) {
          var bMs = Date.parse(pts[fi].t + 'T12:00:00');
          var frac = (bMs - prev.ms) / span;
          if (frac < 0) frac = 0;
          if (frac > 1) frac = 1;
          dataVals[fi] = Math.round((prevV + (firstV - prevV) * frac) * 100) / 100;
        }
      }
    }
    /* In-progress trail (Ryan 2026-06-10, v2): when today has no dot, the
       MAIN line extends to today's x at the window-average height — part of
       the same dataset, so spanGaps draws it with the line's own curvature
       flowing out of the last real dot (a separate 2-point dataset could
       only render straight). Solid like the rest per Ryan; no dot/tooltip
       on the projected end (rawVals guards); the ping rides it and snaps
       to the real dot once today's value lands. */
    var trailEnd = null;
    if (lastRealIdx >= 0 && lastRealIdx < rawVals.length - 1) {
      var trailY = (!isCount && avgVal != null) ? toH(avgVal) : rawVals[lastRealIdx];
      dataVals[rawVals.length - 1] = trailY;
      trailEnd = { index: rawVals.length - 1, y: trailY };
    }
    var mainDataset = {
      label: seriesLabel,
      data: dataVals,
      borderColor: blue,
      backgroundColor: blue,
      borderWidth: 3, // explicit so the legend's line swatch matches exactly
      spanGaps: true,
      tension: 0.3,
      // Dots at the chart floor (y=0) render FULLY instead of clipping to a
      // semi-circle at the plot edge (Ryan 2026-06-10).
      clip: false,
      pointRadius: function (c) { return rawVals[c.dataIndex] == null ? 0 : 3; },
      pointHoverRadius: function (c) { return rawVals[c.dataIndex] == null ? 0 : 5; },
      pointHitRadius: function (c) { return rawVals[c.dataIndex] == null ? 0 : 1; },
    };
    // Verdict coloring, ALL charts (Ryan 2026-06-10): the curve is red
    // wherever it sits above the gray goal line and green wherever it dips
    // below, switching mid-curve at the exact crossing — a Y-axis gradient
    // stroke with a hard stop at the goal pixel (segments can only paint
    // whole point-to-point spans). Dots/tooltip/ping color by their value.
    var durColor = function (v) {
      if (goalY == null) return blue;
      return (v != null && v > goalY) ? red : green;
    };
    if (goalY != null) {
      mainDataset.backgroundColor = green;
      mainDataset.borderColor = function (c) {
        var area = c.chart.chartArea;
        var yScale = c.chart.scales && c.chart.scales.y;
        if (!area || !yScale) return green; // pre-layout pass
        var t = (yScale.getPixelForValue(goalY) - area.top) / (area.bottom - area.top);
        t = Math.max(0, Math.min(1, t));
        var g = c.chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
        g.addColorStop(0, red);
        g.addColorStop(t, red);
        g.addColorStop(t, green);
        g.addColorStop(1, green);
        return g;
      };
      mainDataset.pointBackgroundColor = function (c) { return durColor(c.parsed.y); };
      mainDataset.pointBorderColor = function (c) { return durColor(c.parsed.y); };
    }
    var datasets = [mainDataset];
    // Flat "your average" line — the window-wide TRUE MEAN (same basis as the
    // stat card; a bad day drags it and must be worked off). Hidden under the
    // n<5 gate. Green when beating the goal (below the gray goal line), red
    // when missing it (Ryan 2026-06-10). Count charts (Money Moves) get it
    // too — keeps a freak good/bad day in perspective (Ryan 2026-06-10).
    var avgColor = null;
    if (avgVal != null) {
      avgColor = goalY == null ? blue : (avgVal > goalVal ? red : green);
      var avgY = isCount ? Math.round(avgVal * 10) / 10 : toH(avgVal);
      datasets.push({
        label: 'YOUR AVERAGE',
        data: pts.map(function () { return avgY; }),
        borderColor: avgColor,
        backgroundColor: avgColor,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 0, // no hover dots on the flat lines (Ryan 2026-06-10)
        pointHitRadius: 0,
        fill: false,
      });
    }
    if (goalY != null) {
      datasets.push({
        label: goalLabel || 'YOUR GOAL',
        data: pts.map(function () { return goalY; }),
        borderColor: muted,
        backgroundColor: muted,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 0, // no hover dots on the flat lines (Ryan 2026-06-10)
        pointHitRadius: 0,
        fill: false,
      });
    }
    // Set after chart creation; lets the ping wait for the entry animation.
    var onAnimDone = null;
    var chart = new window.Chart(canvas, {
      type: 'line',
      data: { labels: pts.map(function (p) { return shortDate(p.t); }), datasets: datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: animDuration(canvasId === 'hub-analytics-quotes' ? 'quotes' : 'effort'),
          onComplete: function () { if (onAnimDone) onAnimDone(); },
        },
        // Generous hover: anywhere near a day's x-position pops that day's
        // tooltip (Marketing Ops Daily Performance behavior) — no need to
        // hit the dot itself.
        interaction: { mode: 'index', intersect: false },
        // Room for the custom HTML legend (absolute, bottom of the card) —
        // the canvas ignores DOM siblings when sizing, so the plot clears
        // the legend via in-canvas padding.
        layout: { padding: { bottom: 30 } },
        plugins: {
          // Built-in legend replaced by a custom HTML key (it can only draw
          // straight swatches — Ryan wants the series swatch as a red/green
          // S-curve matching the verdict coloring).
          legend: { display: false },
          tooltip: {
            enabled: false, // canvas tooltip off — frosted HTML popup below
            filter: function (item) { return item.datasetIndex === 0; },
            external: function (context) {
              var tt = context.tooltip;
              var wrap = canvas.parentElement;
              if (!wrap) return;
              var tip = ensureTip(wrap);
              var dp = (tt.dataPoints || []).filter(function (i) { return i.datasetIndex === 0; })[0];
              // No tip on synthetic lead-in / gap days (no real value there).
              if (!tt.opacity || !dp || getVal(pts[dp.dataIndex]) == null) { tip.style.opacity = '0'; return; }
              var p = pts[dp.dataIndex];
              var valColor = durColor(getVal(p));
              // Sample-count line (duration mode): a dot is the bucket MEAN —
              // say how many samples it averages so multi-lead days read as
              // such ('Avg of 3 leads') instead of looking like one event.
              var nLine = '';
              if (!isCount && typeof p.n === 'number' && p.n > 0) {
                var nNoun = (noun || 'item') + (p.n === 1 ? '' : 's');
                nLine = '<div class="hub-analytics__tip-date">' + (p.n === 1 ? '1 ' + nNoun : 'Avg of ' + p.n + ' ' + nNoun) + '</div>';
              }
              tip.innerHTML = '<div class="hub-analytics__tip-date">' + niceDate(p.t, trend.granularity) + '</div>' +
                '<div class="hub-analytics__tip-val" style="color:' + valColor + '">' + tipText(p) + '</div>' + nLine;
              // Anchor to the DATA DOT itself (index-mode caretY averages all
              // lines at that x and could land the tip ON the dot). Float
              // 14px clear above it; flip below when the dot hugs the top.
              var ex = dp.element ? dp.element.x : tt.caretX;
              var ey = dp.element ? dp.element.y : tt.caretY;
              var lx = canvas.offsetLeft + ex;
              // keep the centered tip inside the card horizontally
              var maxL = wrap.clientWidth - 56;
              if (lx < 56) lx = 56;
              if (lx > maxL) lx = maxL;
              tip.style.left = lx + 'px';
              tip.style.top = (canvas.offsetTop + ey) + 'px';
              tip.style.transform = ey < 70
                ? 'translate(-50%, 14px)'
                : 'translate(-50%, calc(-100% - 14px))';
              tip.style.opacity = '1';
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: muted, maxTicksLimit: 10, font: { family: 'Montserrat, sans-serif', size: 11 } },
          },
          y: {
            beginAtZero: true,
            grid: { color: border },
            ticks: {
              color: muted,
              precision: 0,
              font: { family: 'Montserrat, sans-serif', size: 11 },
              callback: function (v) { return isCount ? v : v + 'h'; },
            },
          },
        },
      },
    });
    charts.push(chart);

    /* Custom HTML legend (Ryan 2026-06-10): the series swatch is an S-curve
       colored by the verdict gradient — red on the rise (above goal), green
       on the descent (below) — something the built-in legend can't draw.
       YOUR AVERAGE keeps its dashed verdict-colored swatch, YOUR GOAL the
       solid gray. Absolutely positioned in the in-canvas bottom padding. */
    (function buildLegend() {
      var wrapL = canvas.parentElement;
      if (!wrapL) return;
      var lg = document.createElement('div');
      lg.className = 'hub-analytics__legend';
      var item = function (svg, label) {
        return '<span class="hub-analytics__legend-item">' + svg + esc(label) + '</span>';
      };
      var sCurve = '<svg width="26" height="14" viewBox="0 0 26 14" aria-hidden="true">' +
        '<defs><linearGradient id="ha-lg-verdict" x1="0" y1="0" x2="0" y2="1">' +
        '<stop offset="0.5" stop-color="' + red + '"/><stop offset="0.5" stop-color="' + green + '"/>' +
        '</linearGradient></defs>' +
        '<path d="M1 12 C6 12 8 2 13 2 C18 2 20 12 25 12" fill="none" stroke="url(#ha-lg-verdict)" stroke-width="2.5" stroke-linecap="round"/></svg>';
      var flatLine = function (color, dashed) {
        return '<svg width="26" height="14" viewBox="0 0 26 14" aria-hidden="true">' +
          '<line x1="1" y1="7" x2="25" y2="7" stroke="' + color + '" stroke-width="2"' +
          (dashed ? ' stroke-dasharray="5,4"' : '') + '/></svg>';
      };
      var html = item(goalY != null ? sCurve : flatLine(blue, false), seriesLabel);
      if (avgColor) html += item(flatLine(avgColor, true), 'YOUR AVERAGE');
      if (goalY != null) html += item(flatLine(muted, false), goalLabel || 'YOUR GOAL');
      lg.innerHTML = html;
      wrapL.appendChild(lg);
    })();

    /* Radar ping on the END of the line — a DOM element (CSS keyframes), not
       canvas-drawn, so it can overlap the card padding/edges like the
       Marketing Ops SVG ping (canvas clips at its own boundary — Ryan
       2026-06-10). Sits on the LAST point that has data, which may be a few
       days back when today is empty. */
    // Ping rides the LIVE end of the line: today's dot when one exists, else
    // the end of the dashed in-progress trail (today's x at the average).
    var pingIdx = trailEnd ? trailEnd.index : lastRealIdx;
    var pingY = trailEnd ? trailEnd.y : (lastRealIdx >= 0 ? rawVals[lastRealIdx] : null);
    if (pingIdx >= 0 && pingY != null) {
      var wrap = canvas.parentElement;
      var ping = document.createElement('div');
      ping.className = 'hub-analytics__ping';
      ping.style.borderColor = durColor(pingY);
      ping.style.display = 'none'; // hidden until the entry animation lands
      wrap.appendChild(ping);
      var positionPing = function () {
        var x = chart.scales.x.getPixelForValue(pingIdx);
        var y = chart.scales.y.getPixelForValue(pingY);
        if (!isFinite(x) || !isFinite(y)) { ping.style.display = 'none'; return; }
        ping.style.display = '';
        ping.style.left = (canvas.offsetLeft + x) + 'px';
        ping.style.top = (canvas.offsetTop + y) + 'px';
      };
      // Appear only once the slide-up animation completes — a pre-positioned
      // ping sat stagnant while the line animated into it (Ryan 2026-06-10).
      onAnimDone = positionPing;
      chart.options.onResize = function () { requestAnimationFrame(positionPing); };
    }
  }

  /* ---------------- boot ---------------- */

  function init() {
    root = document.getElementById('hub-analytics-root');
    if (!root) return;
    var session = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession();
    if (!session) return; // the authenticated event below re-runs init
    if (state.loading || state.data) return;
    load();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('rep-portal:authenticated', init);
})();
